import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, isSameMonth, addMonths, subMonths, isToday } from "date-fns";
import { ro } from "date-fns/locale";
import { useAuth } from "@/contexts/AuthContext";
import features from "@/config/features";
import {
  Car, Users, CreditCard, FileText, BarChart3, LogOut, CalendarDays, MessageSquare,
  RefreshCw, Trash2, Edit, Plus, Check, X, Crown, Shield, DollarSign, BookOpen,
  HelpCircle, Save, Upload, Download, Image, Grid3X3, TrendingUp, Video, ImageIcon,
  Tag, Percent, Bike, Truck, Bus, ChevronLeft, ChevronRight, Clock, MapPin, Play, Link,
  AlertCircle, Target, Menu
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Area, AreaChart
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  pending: "bg-yellow-100 text-yellow-800",
  confirmed: "bg-green-100 text-green-800",
  cancelled: "bg-red-100 text-red-800",
  active: "bg-green-100 text-green-800",
  inactive: "bg-slate-100 text-slate-800",
  unread: "bg-blue-100 text-blue-800",
  read: "bg-slate-100 text-slate-800"
};

// Categorii de permis disponibile
const LICENSE_CATEGORIES = ["A", "B", "C", "D", "E", "Tr", "R"];
// Numărul total de chestionare per categorie
const TOTAL_QUIZZES_BY_CAT = { A: 20, B: 47, C: 20, D: 18, E: 7, Tr: 30, R: 23 };

export default function AdminPage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState("dashboard");
  const [stats, setStats] = useState(null);
  const [detailedStats, setDetailedStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [filterQuiz, setFilterQuiz] = useState("");
  const [filterLicenseCat, setFilterLicenseCat] = useState("");

  const [editPlanDialog, setEditPlanDialog] = useState(null);
  const [showPlanUnavailableModal, setShowPlanUnavailableModal] = useState(false);
  const [editQuestionDialog, setEditQuestionDialog] = useState(null);
  const [newQuestionDialog, setNewQuestionDialog] = useState(false);
  const [importDialog, setImportDialog] = useState(false);
  const [viewUserDialog, setViewUserDialog] = useState(null);

  // Video management states
  const [videoChapters, setVideoChapters] = useState([]);
  const [editChapterDialog, setEditChapterDialog] = useState(null);
  const [newChapterDialog, setNewChapterDialog] = useState(false);
  const [editVideoDialog, setEditVideoDialog] = useState(null);
  const [newVideoDialog, setNewVideoDialog] = useState(null); // stores chapter_id

  // Gallery management states
  const [galleryImages, setGalleryImages] = useState([]);
  const [newGalleryDialog, setNewGalleryDialog] = useState(false);
  const [editGalleryDialog, setEditGalleryDialog] = useState(null);

  // Payment requests states
  const [paymentRequests, setPaymentRequests] = useState([]);
  const [paymentSearch, setPaymentSearch] = useState("");
  const [paymentFilter, setPaymentFilter] = useState("all");

  // License categories states
  const [licenseCategories, setLicenseCategories] = useState([]);
  const [editCategoryDialog, setEditCategoryDialog] = useState(null);

  // Free lessons states
  const [freeLessons, setFreeLessons] = useState([]);
  const [newFreeLessonDialog, setNewFreeLessonDialog] = useState(false);
  const [editFreeLessonDialog, setEditFreeLessonDialog] = useState(null);

  // Booking settings states
  const [bookingSettings, setBookingSettings] = useState(null);
  const [editBookingSettings, setEditBookingSettings] = useState(false);

  // Admin calendar states
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [selectedCalendarDay, setSelectedCalendarDay] = useState(null);

  // Materials states
  const [materials, setMaterials] = useState([]);
  const [editMaterialDialog, setEditMaterialDialog] = useState(null);
  const [newMaterialDialog, setNewMaterialDialog] = useState(false);

  // Quiz states
  const [chapterQuizzes, setChapterQuizzes] = useState([]);
  const [editQuizDialog, setEditQuizDialog] = useState(null);
  const [newQuizDialog, setNewQuizDialog] = useState(false);

  const fileInputRef = useRef(null);

  const CHART_COLORS = ["#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899"];

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [statsRes, detailedRes, usersRes, plansRes, questionsRes, bookingsRes, contactsRes, videosRes, galleryRes, paymentsRes, categoriesRes, bookingSettingsRes, materialsRes, quizzesRes, freeLessonsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/stats/detailed`),
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/admin/plans`),
        axios.get(`${API}/admin/questions`),
        axios.get(`${API}/bookings`),
        axios.get(`${API}/contacts`),
        axios.get(`${API}/admin/video-chapters`),
        axios.get(`${API}/admin/gallery`),
        axios.get(`${API}/admin/payment-requests`),
        axios.get(`${API}/admin/license-categories`),
        axios.get(`${API}/admin/booking-settings`),
        axios.get(`${API}/admin/materials`),
        axios.get(`${API}/admin/chapter-quizzes`),
        axios.get(`${API}/admin/free-lessons`)
      ]);
      setStats(statsRes.data);
      setDetailedStats(detailedRes.data);
      setUsers(usersRes.data);
      setPlans(plansRes.data);
      setQuestions(questionsRes.data);
      setBookings(bookingsRes.data);
      setContacts(contactsRes.data);
      setVideoChapters(videosRes.data);
      setGalleryImages(galleryRes.data);
      setPaymentRequests(paymentsRes.data);
      setLicenseCategories(categoriesRes.data);
      setBookingSettings(bookingSettingsRes.data);
      setMaterials(materialsRes.data);
      setChapterQuizzes(quizzesRes.data);
      setFreeLessons(freeLessonsRes.data);
    } catch (error) {
      toast.error("Eroare la încărcarea datelor");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const updateUserRole = async (userId, newRole) => {
    try {
      await axios.patch(`${API}/admin/users/${userId}`, { role: newRole });
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, role: newRole } : u));
      toast.success("Rol actualizat");
    } catch (error) {
      toast.error("Eroare");
    }
  };

  const updateUserSubscription = async (userId, status, plan = null) => {
    try {
      await axios.patch(`${API}/admin/users/${userId}`, { subscription_status: status, subscription_plan: plan });
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, subscription_status: status, subscription_plan: plan } : u));
      toast.success("Actualizat");
    } catch (error) {
      toast.error("Eroare");
    }
  };

  const activateUserPlan = async (userId, planCode) => {
    try {
      await axios.post(`${API}/admin/users/${userId}/activate-${planCode}`);
      fetchAllData();
      toast.success(`Abonament ${planCode.toUpperCase()} activat pentru 30 zile`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Eroare la activare");
    }
  };

  const deactivateUserPlan = async (userId) => {
    try {
      await axios.post(`${API}/admin/users/${userId}/deactivate`);
      fetchAllData();
      toast.success("Abonament dezactivat");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Eroare");
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm("Ștergi utilizatorul?")) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      setUsers(prev => prev.filter(u => u.id !== userId));
      toast.success("Șters");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Eroare");
    }
  };

  const updatePlan = async (planCode, data) => {
    try {
      await axios.patch(`${API}/admin/plans/${planCode}`, data);
      setPlans(prev => prev.map(p => p.code === planCode ? { ...p, ...data } : p));
      setEditPlanDialog(null);
      toast.success("Plan actualizat");
    } catch (error) {
      toast.error("Eroare");
    }
  };

  const createQuestion = async (data) => {
    try {
      await axios.post(`${API}/admin/questions`, data);
      fetchAllData();
      setNewQuestionDialog(false);
      toast.success("Întrebare adăugată");
    } catch (error) {
      toast.error("Eroare");
    }
  };

  const updateQuestion = async (questionId, data) => {
    try {
      await axios.patch(`${API}/admin/questions/${questionId}`, data);
      fetchAllData();
      setEditQuestionDialog(null);
      toast.success("Actualizat");
    } catch (error) {
      toast.error("Eroare");
    }
  };

  const deleteQuestion = async (questionId) => {
    if (!window.confirm("Ștergi întrebarea?")) return;
    try {
      await axios.delete(`${API}/admin/questions/${questionId}`);
      setQuestions(prev => prev.filter(q => q.id !== questionId));
      toast.success("Ștearsă");
    } catch (error) {
      toast.error("Eroare");
    }
  };

  const handleImportCSV = async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/admin/questions/import`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(response.data.message);
      if (response.data.errors?.length > 0) {
        toast.warning(`${response.data.errors.length} erori la import`);
      }
      fetchAllData();
      setImportDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Eroare la import");
    }
  };

  const downloadCSVTemplate = () => {
    const template = `quiz_number,category,text,image_url,option_a,option_b,option_c,option_d,correct_answer,explanation
1,Indicatoare,Ce semnifică acest indicator?,https://example.com/image.jpg,Oprire obligatorie,Cedează trecerea,Drum prioritar,,a,Indicatorul STOP impune oprirea.
1,Prioritate,Care vehicul are prioritate?,,Cel din stânga,Cel din dreapta,Ambele,,b,Regula priorității de dreapta.`;

    const blob = new Blob([template], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'template_intrebari.csv';
    link.click();
  };

  // Video Chapter CRUD
  const createChapter = async (data) => {
    try {
      const res = await axios.post(`${API}/admin/video-chapters`, data);
      setVideoChapters(prev => [...prev, res.data.chapter]);
      setNewChapterDialog(false);
      toast.success("Capitol creat");
      fetchAllData();
    } catch (error) {
      toast.error("Eroare la creare capitol");
    }
  };

  const updateChapter = async (chapterId, data) => {
    try {
      await axios.patch(`${API}/admin/video-chapters/${chapterId}`, data);
      setVideoChapters(prev => prev.map(ch => ch.id === chapterId ? { ...ch, ...data } : ch));
      setEditChapterDialog(null);
      toast.success("Capitol actualizat");
    } catch (error) {
      toast.error("Eroare la actualizare");
    }
  };

  const deleteChapter = async (chapterId) => {
    if (!window.confirm("Ștergi capitolul și toate video-urile din el?")) return;
    try {
      await axios.delete(`${API}/admin/video-chapters/${chapterId}`);
      setVideoChapters(prev => prev.filter(ch => ch.id !== chapterId));
      toast.success("Capitol șters");
    } catch (error) {
      toast.error("Eroare la ștergere");
    }
  };

  // Video CRUD within chapters
  const addVideo = async (chapterId, data) => {
    try {
      const res = await axios.post(`${API}/admin/video-chapters/${chapterId}/videos`, data);
      fetchAllData();
      setNewVideoDialog(null);
      toast.success("Video adăugat");
    } catch (error) {
      toast.error("Eroare la adăugare video");
    }
  };

  const updateVideo = async (chapterId, videoId, data) => {
    try {
      await axios.patch(`${API}/admin/video-chapters/${chapterId}/videos/${videoId}`, data);
      fetchAllData();
      setEditVideoDialog(null);
      toast.success("Video actualizat");
    } catch (error) {
      toast.error("Eroare la actualizare");
    }
  };

  const deleteVideo = async (chapterId, videoId) => {
    if (!window.confirm("Ștergi acest video?")) return;
    try {
      await axios.delete(`${API}/admin/video-chapters/${chapterId}/videos/${videoId}`);
      fetchAllData();
      toast.success("Video șters");
    } catch (error) {
      toast.error("Eroare la ștergere");
    }
  };

  // Gallery CRUD
  const createGalleryImage = async (data) => {
    try {
      const res = await axios.post(`${API}/admin/gallery`, data);
      setGalleryImages(prev => [...prev, res.data.image]);
      setNewGalleryDialog(false);
      toast.success("Imagine adăugată");
    } catch (error) {
      toast.error("Eroare la adăugare imagine");
    }
  };

  const updateGalleryImage = async (imageId, data) => {
    try {
      await axios.patch(`${API}/admin/gallery/${imageId}`, data);
      setGalleryImages(prev => prev.map(img => img.id === imageId ? { ...img, ...data } : img));
      setEditGalleryDialog(null);
      toast.success("Imagine actualizată");
    } catch (error) {
      toast.error("Eroare la actualizare");
    }
  };

  const deleteGalleryImage = async (imageId) => {
    if (!window.confirm("Ștergi imaginea din galerie?")) return;
    try {
      await axios.delete(`${API}/admin/gallery/${imageId}`);
      setGalleryImages(prev => prev.filter(img => img.id !== imageId));
      toast.success("Imagine ștearsă");
    } catch (error) {
      toast.error("Eroare la ștergere");
    }
  };

  // Free Lessons CRUD
  const createFreeLesson = async (data) => {
    try {
      await axios.post(`${API}/admin/free-lessons`, data);
      fetchAllData();
      setNewFreeLessonDialog(false);
      toast.success("Lecție gratuită adăugată");
    } catch (error) {
      toast.error("Eroare la adăugare");
    }
  };

  const updateFreeLesson = async (lessonId, data) => {
    try {
      await axios.patch(`${API}/admin/free-lessons/${lessonId}`, data);
      setFreeLessons(prev => prev.map(l => l.id === lessonId ? { ...l, ...data } : l));
      setEditFreeLessonDialog(null);
      toast.success("Lecție actualizată");
    } catch (error) {
      toast.error("Eroare la actualizare");
    }
  };

  const deleteFreeLesson = async (lessonId) => {
    if (!window.confirm("Ștergi această lecție gratuită?")) return;
    try {
      await axios.delete(`${API}/admin/free-lessons/${lessonId}`);
      setFreeLessons(prev => prev.filter(l => l.id !== lessonId));
      toast.success("Lecție ștearsă");
    } catch (error) {
      toast.error("Eroare la ștergere");
    }
  };

  // Payment requests
  const approvePayment = async (requestId) => {
    if (!window.confirm("Confirmi că ai primit plata și activezi abonamentul?")) return;
    try {
      await axios.post(`${API}/admin/payment-requests/${requestId}/approve`);
      toast.success("Abonament activat!");
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Eroare la aprobare");
    }
  };

  const rejectPayment = async (requestId) => {
    const reason = window.prompt("Motivul respingerii (opțional):");
    if (reason === null) return;
    try {
      await axios.post(`${API}/admin/payment-requests/${requestId}/reject?reason=${encodeURIComponent(reason)}`);
      toast.success("Cerere respinsă");
      fetchAllData();
    } catch (error) {
      toast.error("Eroare la respingere");
    }
  };

  const formatDate = (dateStr) => {
    try {
      return format(new Date(dateStr), "dd MMM yyyy", { locale: ro });
    } catch {
      return dateStr || "-";
    }
  };

  // License Category functions
  const updateCategory = async (categoryId, data) => {
    try {
      await axios.patch(`${API}/admin/license-categories/${categoryId}`, data);
      setLicenseCategories(prev => prev.map(c => c.id === categoryId ? { ...c, ...data } : c));
      setEditCategoryDialog(null);
      toast.success("Categorie actualizată");
    } catch (error) {
      toast.error("Eroare la actualizare");
    }
  };

  const togglePromo = async (categoryId) => {
    try {
      const res = await axios.post(`${API}/admin/license-categories/${categoryId}/toggle-promo`);
      setLicenseCategories(prev => prev.map(c =>
        c.id === categoryId ? { ...c, promo_active: res.data.promo_active } : c
      ));
      toast.success(res.data.message);
    } catch (error) {
      toast.error("Eroare la schimbarea promoției");
    }
  };

  const getCategoryIcon = (iconName) => {
    const icons = {
      bike: Bike,
      car: Car,
      caravan: Car,
      truck: Truck,
      container: Truck,
      bus: Bus
    };
    return icons[iconName] || Car;
  };

  const filteredQuestions = questions.filter(q => {
    if (filterQuiz && q.quiz_number !== parseInt(filterQuiz)) return false;
    if (filterLicenseCat && q.license_category !== filterLicenseCat) return false;
    return true;
  });

  // Sidebar items filtered by version
  const allSidebarItems = [
    { id: "dashboard", name: "Dashboard", icon: BarChart3, show: true },
    { id: "categories", name: "Categorii & Prețuri", icon: Tag, show: features.adminCategories },
    { id: "users", name: "Utilizatori", icon: Users, show: true },
    { id: "plans", name: "Planuri", icon: Crown, show: true },
    { id: "free-lessons", name: "Lecții Gratuite", icon: Play, show: true },
    { id: "videos", name: "Video-uri", icon: Video, show: true },
    { id: "materials", name: "Materiale PDF", icon: FileText, show: features.adminMaterials },
    { id: "quizzes", name: "Quiz-uri", icon: HelpCircle, show: features.adminQuizzes },
    { id: "questions", name: "Chestionare", icon: HelpCircle, show: true },
    { id: "gallery", name: "Galerie", icon: ImageIcon, show: features.adminGallery },
    { id: "bookings", name: "Programări", icon: CalendarDays, show: features.adminBookings },
    { id: "contacts", name: "Mesaje", icon: MessageSquare, show: true },
  ];
  const sidebarItems = allSidebarItems.filter(item => item.show);

  return (
    <div className="min-h-screen bg-slate-100" data-testid="admin-page">
      <div className="flex relative">
        {/* Sidebar */}
        <aside className="hidden lg:block fixed left-0 top-0 h-screen w-64 bg-slate-900 text-white z-40">
          <div className="p-6 border-b border-slate-700">
            <div className="flex items-center gap-2">
              <div className="relative">
                <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                  <Car className="w-6 h-6 text-white" />
                </div>
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-400 rounded-full" />
              </div>
              <div>
                <span className="font-bold text-lg font-['Archivo']">AUTO CORA</span>
                <p className="text-xs text-slate-400">Admin Panel</p>
              </div>
            </div>
          </div>

          <nav className="p-4 space-y-1 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 200px)' }}>
            {sidebarItems.map((item) => (
              <button
                key={item.id}
                onClick={() => setActiveTab(item.id)}
                className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors ${activeTab === item.id ? 'bg-blue-600 text-white' : 'text-slate-300 hover:bg-slate-800'
                  }`}
              >
                <item.icon className="w-5 h-5 flex-shrink-0" />
                <span className="truncate">{item.name}</span>
                {item.id === "questions" && (
                  <Badge className="ml-auto bg-slate-700">{questions.length}</Badge>
                )}
              </button>
            ))}
          </nav>

          <div className="bottom-0 left-0 right-0 p-4 border-t border-slate-700 bg-slate-900">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-9 h-9 bg-slate-700 rounded-full flex items-center justify-center flex-shrink-0">
                <Shield className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{user?.name}</p>
                <Badge className="bg-blue-600">Admin</Badge>
              </div>
            </div>
            <Button onClick={handleLogout} variant="outline" className="w-full border-slate-600 text-slate-300 hover:bg-slate-800 bg-transparent">
              <LogOut className="w-4 h-4 mr-2" />
              Deconectare
            </Button>
          </div>
        </aside>

        {/* Main Content */}
        {/* Mobile top bar */}
        <div className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between">
          <div>
            <h1 className="text-base font-bold text-slate-900 font-['Archivo']">
              {sidebarItems.find(i => i.id === activeTab)?.name}
            </h1>
            <p className="text-xs text-slate-500">Admin Panel</p>
          </div>

          <Button
            variant="outline"
            size="icon"
            onClick={() => setMobileSidebarOpen(true)}
          >
            <Menu className="w-5 h-5" />
          </Button>
        </div>
        {/* Mobile sidebar overlay */}
        {mobileSidebarOpen && (
          <>
            <div
              className="lg:hidden fixed inset-0 bg-black/50 z-40"
              onClick={() => setMobileSidebarOpen(false)}
            />

            <aside className="lg:hidden fixed left-0 top-0 h-screen w-[85%] max-w-xs bg-slate-900 text-white z-50 flex flex-col">
              <div className="p-6 border-b border-slate-700 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                      <Car className="w-6 h-6 text-white" />
                    </div>
                    <div className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-400 rounded-full" />
                  </div>
                  <div>
                    <span className="font-bold text-lg font-['Archivo']">AUTO CORA</span>
                    <p className="text-xs text-slate-400">Admin Panel</p>
                  </div>
                </div>

                <Button
                  variant="ghost"
                  size="icon"
                  className="text-white hover:bg-slate-800"
                  onClick={() => setMobileSidebarOpen(false)}
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>

              <nav className="p-4 space-y-1 overflow-y-auto flex-1">
                {sidebarItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => {
                      setActiveTab(item.id);
                      setMobileSidebarOpen(false);
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${activeTab === item.id
                      ? "bg-blue-600 text-white"
                      : "text-slate-300 hover:bg-slate-800"
                      }`}
                  >
                    <item.icon className="w-5 h-5 flex-shrink-0" />
                    <span className="truncate">{item.name}</span>
                  </button>
                ))}
              </nav>

              <div className="p-4 border-t border-slate-700 bg-slate-900">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-9 h-9 bg-slate-700 rounded-full flex items-center justify-center flex-shrink-0">
                    <Shield className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{user?.name}</p>
                    <Badge className="bg-blue-600">Admin</Badge>
                  </div>
                </div>

                <Button
                  onClick={handleLogout}
                  variant="outline"
                  className="w-full border-slate-600 text-slate-300 hover:bg-slate-800 bg-transparent"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Deconectare
                </Button>
              </div>
            </aside>
          </>
        )}
        <main className="flex-1 p-4 pt-20 lg:pt-8 lg:p-8 lg:ml-64">
          <div className="max-w-7xl mx-auto">
            <div className="hidden lg:flex items-center justify-between mb-8">
              <div>
                <h1 className="text-2xl font-bold text-slate-900 font-['Archivo']">
                  {sidebarItems.find(i => i.id === activeTab)?.name}
                </h1>
                <p className="text-slate-500">Gestionează platforma AUTO CORA</p>
              </div>
              <Button onClick={fetchAllData} variant="outline">
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Reîncarcă
              </Button>
            </div>

            {/* Dashboard */}
            {activeTab === "dashboard" && stats && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <StatCard icon={Users} value={stats.total_users} label="Utilizatori" color="blue" />
                  <StatCard icon={Crown} value={stats.active_subscriptions} label="Abonamente Active" color="green" />
                  <StatCard icon={Video} value={stats.total_chapters_watched || 0} label="Capitole Vizualizate" color="purple" />
                  <StatCard icon={DollarSign} value={`${stats.total_revenue} RON`} label="Venituri" color="yellow" />
                </div>

                {/* Charts Row */}
                {detailedStats && (
                  <div className="grid lg:grid-cols-2 gap-6">
                    {/* Users & Video Activity Over Time */}
                    <Card className="border-0 shadow-sm">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <TrendingUp className="w-5 h-5 text-blue-500" />
                          Activitate (30 zile)
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <ResponsiveContainer width="100%" height={250}>
                          <AreaChart data={detailedStats.chart_data?.slice(-14)}>
                            <defs>
                              <linearGradient id="colorUsers" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                                <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                              </linearGradient>
                              <linearGradient id="colorViews" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                                <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                              </linearGradient>
                              <linearGradient id="colorCompleted" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3} />
                                <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
                              </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                            <XAxis dataKey="date" tickFormatter={(v) => v.slice(5)} fontSize={12} />
                            <YAxis fontSize={12} />
                            <Tooltip
                              contentStyle={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px' }}
                              labelFormatter={(v) => `Data: ${v}`}
                            />
                            <Legend />
                            <Area type="monotone" dataKey="users" name="Utilizatori noi" stroke="#3b82f6" fillOpacity={1} fill="url(#colorUsers)" />
                            <Area type="monotone" dataKey="video_views" name="Vizualizări Video" stroke="#10b981" fillOpacity={1} fill="url(#colorViews)" />
                            <Area type="monotone" dataKey="chapters_completed" name="Capitole Completate" stroke="#8b5cf6" fillOpacity={1} fill="url(#colorCompleted)" />
                          </AreaChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>

                    {/* Subscription Distribution - Start, Standard, Premium, Inactiv */}
                    <Card className="border-0 shadow-sm">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Crown className="w-5 h-5 text-yellow-500" />
                          Distribuție Abonamente
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="flex items-center justify-center">
                          <ResponsiveContainer width="100%" height={200}>
                            <PieChart>
                              <Pie
                                data={[
                                  { name: 'Start', value: detailedStats.subscription_stats?.start || 0 },
                                  { name: 'Standard', value: detailedStats.subscription_stats?.standard || 0 },
                                  { name: 'Premium', value: detailedStats.subscription_stats?.premium || 0 },
                                  { name: 'Inactiv', value: detailedStats.subscription_stats?.inactive || 0 },
                                ].filter(item => item.value > 0)}
                                cx="50%"
                                cy="50%"
                                innerRadius={50}
                                outerRadius={80}
                                paddingAngle={5}
                                dataKey="value"
                                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                                labelLine={false}
                              >
                                <Cell fill="#22c55e" /> {/* Start - verde */}
                                <Cell fill="#3b82f6" /> {/* Standard - albastru */}
                                <Cell fill="#f59e0b" /> {/* Premium - auriu */}
                                <Cell fill="#94a3b8" /> {/* Inactiv - gri */}
                              </Pie>
                              <Tooltip />
                            </PieChart>
                          </ResponsiveContainer>
                        </div>
                        <div className="grid grid-cols-4 gap-2 mt-4 text-center text-sm">
                          <div className="p-2 bg-green-50 rounded-lg">
                            <div className="font-bold text-green-600">{detailedStats.subscription_stats?.start || 0}</div>
                            <div className="text-slate-500 text-xs">Start</div>
                          </div>
                          <div className="p-2 bg-blue-50 rounded-lg">
                            <div className="font-bold text-blue-600">{detailedStats.subscription_stats?.standard || 0}</div>
                            <div className="text-slate-500 text-xs">Standard</div>
                          </div>
                          <div className="p-2 bg-amber-50 rounded-lg">
                            <div className="font-bold text-amber-600">{detailedStats.subscription_stats?.premium || 0}</div>
                            <div className="text-slate-500 text-xs">Premium</div>
                          </div>
                          <div className="p-2 bg-slate-100 rounded-lg">
                            <div className="font-bold text-slate-600">{detailedStats.subscription_stats?.inactive || 0}</div>
                            <div className="text-slate-500 text-xs">Inactiv</div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                )}

                {/* Video Progress & Questions */}
                {detailedStats && (
                  <div className="grid lg:grid-cols-2 gap-6">
                    {/* Video Progress */}
                    <Card className="border-0 shadow-sm">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Video className="w-5 h-5 text-green-500" />
                          Progres Capitole Video
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-2 gap-4 mb-4">
                          <div className="text-center p-4 bg-green-50 rounded-lg">
                            <div className="text-3xl font-bold text-green-600">
                              {detailedStats.video_progress?.completion_rate || 0}%
                            </div>
                            <div className="text-sm text-slate-500">Rată completare</div>
                          </div>
                          <div className="text-center p-4 bg-blue-50 rounded-lg">
                            <div className="text-3xl font-bold text-blue-600">
                              {detailedStats.video_progress?.total_chapters || 0}
                            </div>
                            <div className="text-sm text-slate-500">Total capitole</div>
                          </div>
                        </div>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between p-2 bg-slate-50 rounded">
                            <span>Total vizualizări</span>
                            <span className="font-semibold">{detailedStats.video_progress?.total_views || 0}</span>
                          </div>
                          <div className="flex justify-between p-2 bg-slate-50 rounded">
                            <span>Capitole completate</span>
                            <span className="font-semibold text-green-600">{detailedStats.video_progress?.chapters_completed || 0}</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Top 5 Capitole Populare */}
                    <Card className="border-0 shadow-sm">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Video className="w-5 h-5 text-purple-500" />
                          Top 5 Capitole Populare
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        {detailedStats.chapter_progress?.length > 0 ? (
                          <div className="space-y-3">
                            {[...detailedStats.chapter_progress]
                              .sort((a, b) => b.views - a.views)
                              .slice(0, 5)
                              .map((chapter, idx) => (
                                <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                                  <div className="flex items-center gap-3">
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${idx === 0 ? 'bg-yellow-400 text-yellow-900' :
                                      idx === 1 ? 'bg-slate-300 text-slate-700' :
                                        idx === 2 ? 'bg-amber-600 text-white' :
                                          'bg-slate-200 text-slate-600'
                                      }`}>
                                      {idx + 1}
                                    </div>
                                    <div>
                                      <p className="font-medium text-sm">{chapter.name}</p>
                                      <p className="text-xs text-slate-500">{chapter.total_videos} video-uri</p>
                                    </div>
                                  </div>
                                  <div className="text-right">
                                    <p className="font-bold text-purple-600">{chapter.views} vizualizări</p>
                                    <p className="text-xs text-green-600">{chapter.completed} completate</p>
                                  </div>
                                </div>
                              ))}
                          </div>
                        ) : (
                          <div className="text-center py-8 text-slate-500">
                            <Video className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                            <p>Niciun capitol încă</p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                )}

                <div className="grid lg:grid-cols-2 gap-6">
                  <Card className="border-0 shadow-sm">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Users className="w-5 h-5 text-blue-500" />
                        Top Utilizatori Activi
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {detailedStats?.top_users?.length > 0 ? (
                        <div className="space-y-3">
                          {detailedStats.top_users.slice(0, 5).map((user, idx) => (
                            <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                              <div className="flex items-center gap-3">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${idx === 0 ? 'bg-yellow-400 text-yellow-900' :
                                  idx === 1 ? 'bg-slate-300 text-slate-700' :
                                    idx === 2 ? 'bg-amber-600 text-white' :
                                      'bg-slate-200 text-slate-600'
                                  }`}>
                                  {idx + 1}
                                </div>
                                <div>
                                  <p className="font-medium text-sm">{user.name}</p>
                                  <p className="text-xs text-slate-500">{user.email}</p>
                                </div>
                              </div>
                              <div className="text-right">
                                <p className="font-bold text-blue-600">{user.views} vizualizări</p>
                                <p className="text-xs text-green-600">{user.completed} completate</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-8 text-slate-500">
                          <Users className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                          <p>Niciun utilizator activ încă</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  <Card className="border-0 shadow-sm">
                    <CardHeader>
                      <CardTitle>Statistici Rapide</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="flex justify-between p-3 bg-slate-50 rounded-lg">
                        <span>Total capitole video</span>
                        <span className="font-bold">{detailedStats?.video_progress?.total_chapters || stats?.total_chapters || 0}</span>
                      </div>
                      <div className="flex justify-between p-3 bg-slate-50 rounded-lg">
                        <span>Total video-uri</span>
                        <span className="font-bold">{detailedStats?.video_progress?.total_videos || stats?.total_videos || 0}</span>
                      </div>
                      <div className="flex justify-between p-3 bg-slate-50 rounded-lg">
                        <span>Utilizatori activi azi</span>
                        <span className="font-bold text-green-600">{detailedStats?.video_progress?.users_active_today || stats?.users_active_today || 0}</span>
                      </div>
                      <div className="flex justify-between p-3 bg-slate-50 rounded-lg">
                        <span>Programări în așteptare</span>
                        <span className="font-bold text-yellow-600">{stats?.pending_bookings || 0}</span>
                      </div>
                      <div className="flex justify-between p-3 bg-slate-50 rounded-lg">
                        <span>Mesaje necitite</span>
                        <span className="font-bold text-blue-600">{contacts.filter(c => c.status === 'unread').length}</span>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            )}

            {/* Users */}
            {activeTab === "users" && (
              <Card className="border-0 shadow-sm">
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Nume</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Rol</TableHead>
                        <TableHead>Abonament</TableHead>
                        <TableHead>Data</TableHead>
                        <TableHead className="text-right">Acțiuni</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {users.map((u) => (
                        <TableRow key={u.id}>
                          <TableCell className="font-medium">{u.name}</TableCell>
                          <TableCell>{u.email}</TableCell>
                          <TableCell>
                            <Select value={u.role} onValueChange={(v) => updateUserRole(u.id, v)}>
                              <SelectTrigger className="w-[100px]"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="cursant">Cursant</SelectItem>
                                <SelectItem value="admin">Admin</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell>
                            <Badge className={statusColors[u.subscription_status]}>
                              {u.subscription_status === "active" ? `${u.subscription_plan}` : "Inactiv"}
                            </Badge>
                          </TableCell>
                          <TableCell>{formatDate(u.created_at)}</TableCell>
                          <TableCell className="text-right">
                            <Button size="sm" variant="ghost" onClick={() => setViewUserDialog(u)}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="ghost" className="text-red-600" onClick={() => deleteUser(u.id)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}

            {/* Payments Tab */}
            {activeTab === "payments" && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <div>
                    <h2 className="text-xl font-bold">Cereri de Plată</h2>
                    <p className="text-slate-500 text-sm">Verifică plățile și activează abonamentele</p>
                  </div>
                  <Badge className="bg-yellow-100 text-yellow-800">
                    {paymentRequests.filter(p => p.status === "pending").length} în așteptare
                  </Badge>
                </div>

                {/* Search and Filter */}
                <div className="flex flex-col lg:flex-row gap-4 lg:items-center">
                  <div className="relative flex-1 max-w-md">
                    <input
                      type="text"
                      placeholder="Caută după cod plată (ex: A4BCBE57)..."
                      className="w-full px-4 py-2 pl-10 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={paymentSearch || ""}
                      onChange={(e) => setPaymentSearch(e.target.value.toUpperCase())}
                    />
                    <svg className="absolute left-3 top-2.5 w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  <select
                    className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={paymentFilter || "all"}
                    onChange={(e) => setPaymentFilter(e.target.value)}
                  >
                    <option value="all">Toate</option>
                    <option value="pending">În așteptare</option>
                    <option value="approved">Aprobate</option>
                    <option value="rejected">Respinse</option>
                  </select>
                </div>

                {(() => {
                  const filtered = paymentRequests.filter(r => {
                    const matchesSearch = !paymentSearch || r.id.includes(paymentSearch) || r.user_email.toLowerCase().includes(paymentSearch.toLowerCase());
                    const matchesFilter = paymentFilter === "all" || !paymentFilter || r.status === paymentFilter;
                    return matchesSearch && matchesFilter;
                  });

                  return filtered.length > 0 ? (
                    <div className="space-y-4">
                      {filtered.map((request) => (
                        <Card key={request.id} className={`border-0 shadow-sm ${request.status === "pending" ? "border-l-4 border-l-yellow-500" :
                          request.status === "approved" ? "border-l-4 border-l-green-500" :
                            "border-l-4 border-l-red-500"
                          }`}>
                          <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-4">
                                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${request.status === "pending" ? "bg-yellow-100" :
                                  request.status === "approved" ? "bg-green-100" : "bg-red-100"
                                  }`}>
                                  <CreditCard className={`w-6 h-6 ${request.status === "pending" ? "text-yellow-600" :
                                    request.status === "approved" ? "text-green-600" : "text-red-600"
                                    }`} />
                                </div>
                                <div>
                                  <p className="font-semibold">{request.user_name || request.user_email}</p>
                                  <p className="text-sm text-slate-500">{request.user_email}</p>
                                  <div className="flex items-center gap-2 mt-1">
                                    <Badge variant="outline">{request.plan_name}</Badge>
                                    <code className="text-xs bg-slate-100 px-2 py-0.5 rounded font-mono">
                                      COD: {request.id}
                                    </code>
                                  </div>
                                </div>
                              </div>
                              <div className="w-full lg:w-auto lg:text-right">
                                <p className="text-2xl font-bold">{request.amount} RON</p>
                                <p className="text-sm text-slate-500">{formatDate(request.created_at)}</p>
                                {request.status === "pending" ? (
                                  <div className="flex gap-2 mt-2">
                                    <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => approvePayment(request.id)}>
                                      <Check className="w-4 h-4 mr-1" /> Aprobă
                                    </Button>
                                    <Button size="sm" variant="destructive" onClick={() => rejectPayment(request.id)}>
                                      <X className="w-4 h-4 mr-1" /> Respinge
                                    </Button>
                                  </div>
                                ) : (
                                  <Badge className={request.status === "approved" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}>
                                    {request.status === "approved" ? "Aprobat" : "Respins"}
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  ) : (
                    <Card className="border-0 shadow-sm">
                      <CardContent className="py-16 text-center">
                        <CreditCard className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                        <h3 className="font-semibold text-slate-700">
                          {paymentSearch || paymentFilter !== "all" ? "Nicio cerere găsită" : "Nicio cerere de plată"}
                        </h3>
                        <p className="text-slate-500">
                          {paymentSearch ? `Nu există cereri cu codul "${paymentSearch}"` : "Cererile de la cursanți vor apărea aici"}
                        </p>
                      </CardContent>
                    </Card>
                  );
                })()}
              </div>
            )}

            {/* Plans */}
            {activeTab === "plans" && (
              <div className="space-y-6">
                {/* Unavailable Modal Overlay */}
                {showPlanUnavailableModal && (
                  <Dialog open={showPlanUnavailableModal} onOpenChange={setShowPlanUnavailableModal}>
                    <DialogContent className="sm:max-w-md">
                      <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-amber-600">
                          <AlertCircle className="w-5 h-5" />
                          Momentan Indisponibil
                        </DialogTitle>
                      </DialogHeader>
                      <div className="py-4">
                        <p className="text-slate-600">
                          Editarea planurilor de abonament nu este disponibilă momentan. Această funcție va fi activată în curând.
                        </p>
                      </div>
                      <div className="flex justify-end">
                        <Button onClick={() => setShowPlanUnavailableModal(false)}>
                          Am înțeles
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                )}

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {plans.map((plan) => (
                    <Card key={plan.id} className="border-0 shadow-sm">
                      <CardHeader>
                        <div className="flex items-center justify-between">
                          <CardTitle className="flex items-center gap-2">
                            {plan.code === "premium" ? <Crown className="w-5 h-5 text-yellow-500" /> :
                              plan.code === "start" ? <Target className="w-5 h-5 text-slate-500" /> :
                                <CreditCard className="w-5 h-5 text-blue-500" />}
                            {plan.name}
                          </CardTitle>
                          <Button size="sm" variant="outline" onClick={() => setShowPlanUnavailableModal(true)}>
                            <Edit className="w-4 h-4" />
                          </Button>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="text-3xl font-bold mb-4">
                          {plan.price === 0 ? (
                            <span className="text-green-600">Gratuit</span>
                          ) : (
                            <>{plan.price} <span className="text-lg font-normal text-slate-500">{plan.currency}/lună</span></>
                          )}
                        </div>
                        <div className="space-y-2">
                          {plan.features?.map((f, i) => (
                            <div key={i} className="flex items-center gap-2 text-sm">
                              <Check className="w-4 h-4 text-green-500" />
                              {f}
                            </div>
                          ))}
                          <div className="flex items-center gap-2 text-sm">
                            <Check className="w-4 h-4 text-green-500" />
                            {plan.tests_per_day === -1 ? "Teste nelimitate" : `${plan.tests_per_day} teste/zi`}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Categories & Prices */}
            {activeTab === "categories" && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <div>
                    <h2 className="text-xl font-bold">Categorii Permis & Prețuri</h2>
                    <p className="text-slate-500 text-sm">Editează prețurile și gestionează promoțiile</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-green-100 text-green-800">
                      {licenseCategories.filter(c => c.promo_active).length} promoții active
                    </Badge>
                  </div>
                </div>

                <div className="grid gap-4">
                  {licenseCategories.map((category) => {
                    const IconComponent = getCategoryIcon(category.icon);
                    return (
                      <Card key={category.id} className={`border-0 shadow-sm ${category.promo_active ? 'ring-2 ring-green-500' : ''}`}>
                        <CardContent className="p-6">
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-4">
                              <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${category.code === 'A' ? 'bg-gradient-to-br from-orange-500 to-red-500' :
                                category.code === 'B' ? 'bg-gradient-to-br from-blue-500 to-blue-700' :
                                  category.code === 'B+E' ? 'bg-gradient-to-br from-teal-500 to-cyan-600' :
                                    category.code === 'C' ? 'bg-gradient-to-br from-amber-500 to-orange-600' :
                                      category.code === 'C+E' ? 'bg-gradient-to-br from-slate-600 to-slate-800' :
                                        'bg-gradient-to-br from-purple-500 to-indigo-600'
                                }`}>
                                <IconComponent className="w-7 h-7 text-white" />
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <h3 className="text-lg font-bold">{category.name}</h3>
                                  <Badge variant="outline">{category.code}</Badge>
                                  {category.promo_active && (
                                    <Badge className="bg-green-500 text-white">
                                      <Percent className="w-3 h-3 mr-1" />
                                      PROMOȚIE
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-slate-500 text-sm mt-1">{category.description}</p>
                                <div className="flex items-center gap-4 mt-3">
                                  <div className="text-sm">
                                    <span className="text-slate-500">Durată:</span>
                                    <span className="ml-1 font-medium">{category.duration}</span>
                                  </div>
                                  <div className="text-sm">
                                    <span className="text-slate-500">Preț:</span>
                                    {category.promo_active && category.promo_price_min ? (
                                      <>
                                        <span className="ml-1 line-through text-slate-400">{category.price_min}-{category.price_max} RON</span>
                                        <span className="ml-2 font-bold text-green-600">{category.promo_price_min}-{category.promo_price_max} RON</span>
                                      </>
                                    ) : (
                                      <span className="ml-1 font-bold">{category.price_min}-{category.price_max} RON</span>
                                    )}
                                  </div>
                                </div>
                                {category.promo_active && category.promo_text && (
                                  <div className="mt-2 inline-block px-3 py-1 bg-green-50 text-green-700 text-sm rounded-full">
                                    {category.promo_text}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                size="sm"
                                variant={category.promo_active ? "default" : "outline"}
                                className={category.promo_active ? "bg-green-600 hover:bg-green-700" : ""}
                                onClick={() => togglePromo(category.id)}
                              >
                                <Percent className="w-4 h-4 mr-1" />
                                {category.promo_active ? "Dezactivează" : "Activează"} Promoție
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => setEditCategoryDialog(category)}>
                                <Edit className="w-4 h-4 mr-1" />
                                Editează
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>

                {licenseCategories.length === 0 && (
                  <Card className="border-0 shadow-sm">
                    <CardContent className="py-16 text-center">
                      <Tag className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                      <h3 className="font-semibold text-slate-700">Nicio categorie</h3>
                      <p className="text-slate-500">Categoriile vor fi create automat la prima încărcare</p>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}

            {/* Questions */}
            {activeTab === "questions" && (
              <div className="space-y-4">
                <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 items-stretch sm:items-center">
                  <Select value={filterLicenseCat || "all"} onValueChange={(v) => setFilterLicenseCat(v === "all" ? "" : v)}>
                    <SelectTrigger className="w-[140px]">
                      <SelectValue placeholder="Cat. Permis" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Toate</SelectItem>
                      {LICENSE_CATEGORIES.map(cat => (
                        <SelectItem key={cat} value={cat}>Cat. {cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Select value={filterQuiz || "all"} onValueChange={(v) => setFilterQuiz(v === "all" ? "" : v)}>
                    <SelectTrigger className="w-[160px]">
                      <SelectValue placeholder="Chestionar" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Toate</SelectItem>
                      {Array.from({ length: TOTAL_QUIZZES_BY_CAT[filterLicenseCat] || 47 }, (_, i) => (
                        <SelectItem key={i} value={String(i + 1)}>Chestionar {i + 1}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <div className="flex flex-col sm:flex-row gap-2 sm:ml-auto w-full sm:w-auto">
                    <Button variant="outline" onClick={() => setImportDialog(true)}>
                      <Upload className="w-4 h-4 mr-2" />
                      Import CSV
                    </Button>
                    <Button onClick={() => setNewQuestionDialog(true)} className="bg-blue-600 hover:bg-blue-700">
                      <Plus className="w-4 h-4 mr-2" />
                      Adaugă
                    </Button>
                  </div>
                </div>

                <Card className="border-0 shadow-sm">
                  <CardContent className="p-0">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-12">Perm</TableHead>
                          <TableHead className="w-12">Ch.</TableHead>
                          <TableHead className="w-12">Img</TableHead>
                          <TableHead>Întrebare</TableHead>
                          <TableHead className="text-right">Acțiuni</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredQuestions.slice(0, 100).map((q) => (
                          <TableRow key={q.id}>
                            <TableCell>
                              <Badge variant="secondary" className="text-xs">{q.license_category || "B"}</Badge>
                            </TableCell>
                            <TableCell>
                              {q.quiz_number ? (
                                <Badge variant="outline">{q.quiz_number}</Badge>
                              ) : '-'}
                            </TableCell>
                            <TableCell>
                              {q.image_url ? (
                                <img src={q.image_url} alt="" className="w-10 h-10 object-cover rounded" onError={(e) => e.target.style.display = 'none'} />
                              ) : (
                                <span className="text-slate-400">-</span>
                              )}
                            </TableCell>
                            <TableCell className="max-w-md truncate">{q.text}</TableCell>
                            <TableCell className="text-right">
                              <Button size="sm" variant="ghost" onClick={() => setEditQuestionDialog(q)}>
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button size="sm" variant="ghost" className="text-red-600" onClick={() => deleteQuestion(q.id)}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    {filteredQuestions.length > 100 && (
                      <p className="p-4 text-center text-slate-500">
                        Afișate 100 din {filteredQuestions.length} întrebări
                      </p>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Bookings */}
            {activeTab === "bookings" && (
              <div className="space-y-6">
                {/* Booking Settings Card */}
                <Card className="border-0 shadow-sm">
                  <CardHeader>
                    <div className="flex justify-between items-center">
                      <div>
                        <CardTitle className="text-lg">Setări Programări</CardTitle>
                        <p className="text-sm text-slate-500">Configurează disponibilitatea și limita zilnică</p>
                      </div>
                      <Button
                        variant={editBookingSettings ? "default" : "outline"}
                        onClick={() => setEditBookingSettings(!editBookingSettings)}
                      >
                        {editBookingSettings ? "Închide" : "Editează"}
                      </Button>
                    </div>
                  </CardHeader>
                  {editBookingSettings && bookingSettings && (
                    <CardContent className="space-y-6">
                      {/* Max per day */}
                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <Label className="text-sm font-medium">Limită programări / zi</Label>
                          <Input
                            type="number"
                            min="1"
                            max="50"
                            value={bookingSettings.max_bookings_per_day}
                            onChange={(e) => setBookingSettings(prev => ({
                              ...prev,
                              max_bookings_per_day: parseInt(e.target.value) || 5
                            }))}
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-sm font-medium">Program de lucru</Label>
                          <div className="flex gap-2 mt-1">
                            <Input
                              type="time"
                              value={bookingSettings.working_hours?.start || "09:00"}
                              onChange={(e) => setBookingSettings(prev => ({
                                ...prev,
                                working_hours: { ...prev.working_hours, start: e.target.value }
                              }))}
                            />
                            <span className="self-center">-</span>
                            <Input
                              type="time"
                              value={bookingSettings.working_hours?.end || "17:00"}
                              onChange={(e) => setBookingSettings(prev => ({
                                ...prev,
                                working_hours: { ...prev.working_hours, end: e.target.value }
                              }))}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Available days */}
                      <div>
                        <Label className="text-sm font-medium mb-3 block">Zile disponibile</Label>
                        <div className="flex flex-wrap gap-2">
                          {[
                            { key: "monday", label: "Luni" },
                            { key: "tuesday", label: "Marți" },
                            { key: "wednesday", label: "Miercuri" },
                            { key: "thursday", label: "Joi" },
                            { key: "friday", label: "Vineri" },
                            { key: "saturday", label: "Sâmbătă" },
                            { key: "sunday", label: "Duminică" }
                          ].map(day => (
                            <Button
                              key={day.key}
                              type="button"
                              variant={bookingSettings.available_days?.[day.key] ? "default" : "outline"}
                              className={bookingSettings.available_days?.[day.key] ? "bg-green-600 hover:bg-green-700" : ""}
                              onClick={() => setBookingSettings(prev => ({
                                ...prev,
                                available_days: {
                                  ...prev.available_days,
                                  [day.key]: !prev.available_days?.[day.key]
                                }
                              }))}
                            >
                              {day.label}
                            </Button>
                          ))}
                        </div>
                      </div>

                      {/* Time slots */}
                      <div>
                        <Label className="text-sm font-medium mb-3 block">Ore disponibile (slot-uri)</Label>
                        <div className="flex flex-wrap gap-2">
                          {["08:00", "09:00", "10:00", "11:00", "12:00", "13:00", "14:00", "15:00", "16:00", "17:00", "18:00"].map(time => (
                            <Button
                              key={time}
                              type="button"
                              size="sm"
                              variant={bookingSettings.time_slots?.includes(time) ? "default" : "outline"}
                              className={bookingSettings.time_slots?.includes(time) ? "bg-blue-600 hover:bg-blue-700" : ""}
                              onClick={() => {
                                const slots = bookingSettings.time_slots || [];
                                const newSlots = slots.includes(time)
                                  ? slots.filter(s => s !== time)
                                  : [...slots, time].sort();
                                setBookingSettings(prev => ({ ...prev, time_slots: newSlots }));
                              }}
                            >
                              {time}
                            </Button>
                          ))}
                        </div>
                      </div>

                      {/* Save button */}
                      <Button
                        className="bg-blue-600 hover:bg-blue-700"
                        onClick={async () => {
                          try {
                            await axios.patch(`${API}/admin/booking-settings`, {
                              max_bookings_per_day: bookingSettings.max_bookings_per_day,
                              available_days: bookingSettings.available_days,
                              working_hours: bookingSettings.working_hours,
                              time_slots: bookingSettings.time_slots
                            });
                            toast.success("Setări salvate!");
                            setEditBookingSettings(false);
                          } catch (err) {
                            toast.error("Eroare la salvare");
                          }
                        }}
                      >
                        <Save className="w-4 h-4 mr-2" />
                        Salvează Setările
                      </Button>
                    </CardContent>
                  )}
                  {!editBookingSettings && bookingSettings && (
                    <CardContent>
                      <div className="flex flex-wrap gap-4 text-sm">
                        <div className="bg-slate-100 px-3 py-2 rounded-lg">
                          <span className="text-slate-500">Limită/zi:</span>
                          <span className="ml-2 font-semibold">{bookingSettings.max_bookings_per_day}</span>
                        </div>
                        <div className="bg-slate-100 px-3 py-2 rounded-lg">
                          <span className="text-slate-500">Program:</span>
                          <span className="ml-2 font-semibold">{bookingSettings.working_hours?.start} - {bookingSettings.working_hours?.end}</span>
                        </div>
                        <div className="bg-slate-100 px-3 py-2 rounded-lg">
                          <span className="text-slate-500">Slot-uri:</span>
                          <span className="ml-2 font-semibold">{bookingSettings.time_slots?.length || 0} ore</span>
                        </div>
                      </div>
                    </CardContent>
                  )}
                </Card>

                {/* Admin Calendar View */}
                <Card className="border-0 shadow-sm">
                  <CardHeader>
                    <div className="flex justify-between items-center">
                      <div>
                        <CardTitle className="text-lg flex items-center gap-2">
                          <CalendarDays className="w-5 h-5 text-blue-600" />
                          Calendar Programări
                        </CardTitle>
                        <p className="text-sm text-slate-500">Vedere rapidă a programărilor pe lună</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCalendarMonth(subMonths(calendarMonth, 1))}
                        >
                          <ChevronLeft className="w-4 h-4" />
                        </Button>
                        <span className="font-semibold min-w-[140px] text-center">
                          {format(calendarMonth, "MMMM yyyy", { locale: ro })}
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCalendarMonth(addMonths(calendarMonth, 1))}
                        >
                          <ChevronRight className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {/* Legend */}
                    <div className="flex flex-wrap gap-4 mb-6 pb-4 border-b">
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded bg-green-500"></div>
                        <span className="text-sm text-slate-600">Confirmate</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded bg-yellow-500"></div>
                        <span className="text-sm text-slate-600">În așteptare</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded bg-slate-200"></div>
                        <span className="text-sm text-slate-600">Liber</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded border-2 border-blue-500"></div>
                        <span className="text-sm text-slate-600">Azi</span>
                      </div>
                    </div>

                    {/* Calendar Grid */}
                    <div className="grid grid-cols-7 gap-1">
                      {/* Day headers */}
                      {["Lu", "Ma", "Mi", "Jo", "Vi", "Sâ", "Du"].map(day => (
                        <div key={day} className="text-center text-sm font-semibold text-slate-500 py-2">
                          {day}
                        </div>
                      ))}

                      {/* Calendar days */}
                      {(() => {
                        const monthStart = startOfMonth(calendarMonth);
                        const monthEnd = endOfMonth(calendarMonth);
                        const days = eachDayOfInterval({ start: monthStart, end: monthEnd });

                        // Add empty cells for days before month start
                        const startDay = monthStart.getDay();
                        const emptyDays = startDay === 0 ? 6 : startDay - 1;

                        const allCells = [
                          ...Array(emptyDays).fill(null),
                          ...days
                        ];

                        return allCells.map((day, idx) => {
                          if (!day) {
                            return <div key={`empty-${idx}`} className="h-20"></div>;
                          }

                          const dateStr = format(day, "yyyy-MM-dd");
                          const dayBookings = bookings.filter(b => b.preferred_date === dateStr);
                          const confirmedCount = dayBookings.filter(b => b.status === "confirmed").length;
                          const pendingCount = dayBookings.filter(b => b.status === "pending").length;
                          const hasBookings = dayBookings.length > 0;
                          const isSelected = selectedCalendarDay && isSameDay(day, selectedCalendarDay);
                          const isTodayDate = isToday(day);

                          let bgColor = "bg-slate-50 hover:bg-slate-100";
                          if (confirmedCount > 0) bgColor = "bg-green-100 hover:bg-green-200";
                          else if (pendingCount > 0) bgColor = "bg-yellow-100 hover:bg-yellow-200";

                          return (
                            <div
                              key={dateStr}
                              onClick={() => setSelectedCalendarDay(isSelected ? null : day)}
                              className={`h-20 p-1 rounded-lg cursor-pointer transition-all ${bgColor} ${isSelected ? "ring-2 ring-blue-500" : ""
                                } ${isTodayDate ? "ring-2 ring-blue-500 ring-offset-1" : ""}`}
                            >
                              <div className="flex justify-between items-start">
                                <span className={`text-sm font-medium ${isTodayDate ? "text-blue-600" : "text-slate-700"}`}>
                                  {format(day, "d")}
                                </span>
                                {hasBookings && (
                                  <Badge className="text-xs px-1 py-0 bg-slate-700">
                                    {dayBookings.length}
                                  </Badge>
                                )}
                              </div>
                              {hasBookings && (
                                <div className="mt-1 space-y-0.5">
                                  {confirmedCount > 0 && (
                                    <div className="flex items-center gap-1">
                                      <div className="w-2 h-2 rounded-full bg-green-500"></div>
                                      <span className="text-xs text-green-700">{confirmedCount} conf.</span>
                                    </div>
                                  )}
                                  {pendingCount > 0 && (
                                    <div className="flex items-center gap-1">
                                      <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
                                      <span className="text-xs text-yellow-700">{pendingCount} așt.</span>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        });
                      })()}
                    </div>

                    {/* Selected day details */}
                    {selectedCalendarDay && (
                      <div className="mt-6 pt-6 border-t">
                        <h4 className="font-semibold text-lg mb-4 flex items-center gap-2">
                          <CalendarDays className="w-5 h-5 text-blue-600" />
                          {format(selectedCalendarDay, "EEEE, dd MMMM yyyy", { locale: ro })}
                        </h4>
                        {(() => {
                          const dateStr = format(selectedCalendarDay, "yyyy-MM-dd");
                          const dayBookings = bookings.filter(b => b.preferred_date === dateStr);

                          if (dayBookings.length === 0) {
                            return (
                              <div className="text-center py-8 text-slate-500">
                                <CalendarDays className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                                <p>Nicio programare în această zi</p>
                              </div>
                            );
                          }

                          // Sort by time
                          const sortedBookings = [...dayBookings].sort((a, b) =>
                            a.preferred_time.localeCompare(b.preferred_time)
                          );

                          return (
                            <div className="space-y-3">
                              {sortedBookings.map(booking => (
                                <div
                                  key={booking.id}
                                  className={`p-4 rounded-lg border ${booking.status === "confirmed" ? "bg-green-50 border-green-200" :
                                    booking.status === "pending" ? "bg-yellow-50 border-yellow-200" :
                                      "bg-slate-50 border-slate-200"
                                    }`}
                                >
                                  <div className="flex justify-between items-start">
                                    <div className="flex items-start gap-3">
                                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${booking.status === "confirmed" ? "bg-green-500" :
                                        booking.status === "pending" ? "bg-yellow-500" : "bg-slate-400"
                                        } text-white font-bold`}>
                                        {booking.preferred_time.split(":")[0]}
                                      </div>
                                      <div>
                                        <p className="font-semibold text-slate-900">{booking.name}</p>
                                        <div className="flex items-center gap-2 text-sm text-slate-600 mt-1">
                                          <Clock className="w-4 h-4" />
                                          <span>{booking.preferred_time}</span>
                                          <Badge variant="outline" className="ml-2">{booking.license_category}</Badge>
                                        </div>
                                        <p className="text-sm text-slate-500 mt-1">{booking.phone} • {booking.email}</p>
                                      </div>
                                    </div>
                                    <Badge className={
                                      booking.status === "confirmed" ? "bg-green-500" :
                                        booking.status === "pending" ? "bg-yellow-500" : "bg-slate-400"
                                    }>
                                      {booking.status === "confirmed" ? "Confirmat" :
                                        booking.status === "pending" ? "În așteptare" : "Anulat"}
                                    </Badge>
                                  </div>
                                </div>
                              ))}
                            </div>
                          );
                        })()}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Bookings Table */}
                <Card className="border-0 shadow-sm">
                  <CardHeader>
                    <CardTitle className="text-lg">Programări ({bookings.length})</CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Nume</TableHead>
                          <TableHead>Contact</TableHead>
                          <TableHead>Categorie</TableHead>
                          <TableHead>Data</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead className="text-right">Acțiuni</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {bookings.map((b) => (
                          <TableRow key={b.id}>
                            <TableCell className="font-medium">{b.name}</TableCell>
                            <TableCell><div className="text-sm">{b.phone}<br /><span className="text-slate-500">{b.email}</span></div></TableCell>
                            <TableCell><Badge variant="outline">{b.license_category}</Badge></TableCell>
                            <TableCell>{formatDate(b.preferred_date)} {b.preferred_time}</TableCell>
                            <TableCell>
                              <Select value={b.status} onValueChange={async (v) => {
                                await axios.patch(`${API}/bookings/${b.id}`, { status: v });
                                setBookings(prev => prev.map(x => x.id === b.id ? { ...x, status: v } : x));
                              }}>
                                <SelectTrigger className={`w-[120px] ${statusColors[b.status]}`}><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="pending">În așteptare</SelectItem>
                                  <SelectItem value="confirmed">Confirmat</SelectItem>
                                  <SelectItem value="cancelled">Anulat</SelectItem>
                                </SelectContent>
                              </Select>
                            </TableCell>
                            <TableCell className="text-right">
                              <Button size="sm" variant="ghost" className="text-red-600" onClick={async () => {
                                if (window.confirm("Ștergi?")) {
                                  await axios.delete(`${API}/bookings/${b.id}`);
                                  setBookings(prev => prev.filter(x => x.id !== b.id));
                                }
                              }}><Trash2 className="w-4 h-4" /></Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Contacts */}
            {activeTab === "contacts" && (
              <Card className="border-0 shadow-sm">
                <CardContent className="p-0 overflow-x-auto">
                  <div className="min-w-[900px]">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Nume</TableHead>
                          <TableHead>Contact</TableHead>
                          <TableHead>Subiect</TableHead>
                          <TableHead>Data</TableHead>
                          <TableHead className="text-right">Acțiuni</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {contacts.map((c) => (
                          <TableRow key={c.id}>
                            <TableCell className="font-medium">{c.name}</TableCell>
                            <TableCell><div className="text-sm">{c.phone}<br /><span className="text-slate-500">{c.email}</span></div></TableCell>
                            <TableCell className="max-w-xs truncate">{c.subject}</TableCell>
                            <TableCell>{formatDate(c.created_at)}</TableCell>
                            <TableCell className="text-right">
                              <Button size="sm" variant="ghost" className="text-red-600" onClick={async () => {
                                if (window.confirm("Ștergi?")) {
                                  await axios.delete(`${API}/contacts/${c.id}`);
                                  setContacts(prev => prev.filter(x => x.id !== c.id));
                                }
                              }}><Trash2 className="w-4 h-4" /></Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    </div>
                </CardContent>
              </Card>
            )}

            {/* Free Lessons Tab */}
            {activeTab === "free-lessons" && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <div>
                    <h2 className="text-xl font-bold">Lecții Gratuite</h2>
                    <p className="text-slate-500 text-sm">Adaugă lecții video gratuite pentru vizitatori</p>
                  </div>
                  <Button onClick={() => setNewFreeLessonDialog(true)} className="bg-blue-600 hover:bg-blue-700">
                    <Plus className="w-4 h-4 mr-2" />
                    Adaugă Lecție
                  </Button>
                </div>

                {freeLessons.length === 0 ? (
                  <Card>
                    <CardContent className="py-12 text-center">
                      <Play className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                      <h3 className="font-semibold text-slate-700 mb-2">Nicio lecție gratuită</h3>
                      <p className="text-slate-500 text-sm mb-4">Adaugă lecții video gratuite pentru a atrage vizitatori</p>
                      <Button onClick={() => setNewFreeLessonDialog(true)} variant="outline">
                        <Plus className="w-4 h-4 mr-2" />
                        Adaugă Prima Lecție
                      </Button>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid gap-4">
                    {freeLessons.map((lesson, idx) => (
                      <Card key={lesson.id} className="overflow-hidden">
                        <CardContent className="p-0">
                          <div className="flex items-start gap-4 p-4">
                            {/* Thumbnail */}
                            <div className="w-40 h-24 bg-slate-200 rounded-lg flex-shrink-0 overflow-hidden">
                              {lesson.thumbnail_url ? (
                                <img src={lesson.thumbnail_url} alt={lesson.title} className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-500 to-blue-600">
                                  <Play className="w-8 h-8 text-white" />
                                </div>
                              )}
                            </div>

                            {/* Info */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between">
                                <div>
                                  <h3 className="font-semibold text-slate-900">{lesson.title}</h3>
                                  {lesson.description && (
                                    <p className="text-slate-500 text-sm mt-1 line-clamp-2">{lesson.description}</p>
                                  )}
                                  <div className="flex items-center gap-4 mt-2 text-sm text-slate-500">
                                    {lesson.duration && (
                                      <span className="flex items-center gap-1">
                                        <Clock className="w-4 h-4" />
                                        {lesson.duration}
                                      </span>
                                    )}
                                    <span>Ordine: {lesson.order}</span>
                                    <Badge variant={lesson.is_active ? "default" : "secondary"}>
                                      {lesson.is_active ? "Activ" : "Inactiv"}
                                    </Badge>
                                  </div>
                                  {lesson.video_url && (
                                    <p className="text-xs text-blue-600 mt-1 truncate max-w-md">
                                      <Link className="w-3 h-3 inline mr-1" />
                                      {lesson.video_url}
                                    </p>
                                  )}
                                </div>
                                <div className="flex gap-2">
                                  <Button variant="outline" size="sm" onClick={() => setEditFreeLessonDialog(lesson)}>
                                    <Edit className="w-4 h-4" />
                                  </Button>
                                  <Button variant="outline" size="sm" className="text-red-600 hover:bg-red-50" onClick={() => deleteFreeLesson(lesson.id)}>
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Videos Tab */}
            {activeTab === "videos" && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <div>
                    <h2 className="text-xl font-bold">Gestionare Video-uri</h2>
                    <p className="text-slate-500 text-sm">Adaugă și editează capitole și lecții video</p>
                  </div>
                  <Button onClick={() => setNewChapterDialog(true)} className="bg-blue-600 hover:bg-blue-700">
                    <Plus className="w-4 h-4 mr-2" />
                    Capitol Nou
                  </Button>
                </div>

                {videoChapters.map((chapter) => (
                  <Card key={chapter.id} className="border-0 shadow-sm">
                    <CardHeader className="border-b bg-slate-50">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center font-bold">
                            {chapter.order}
                          </div>
                          <div>
                            <CardTitle className="text-lg">{chapter.title}</CardTitle>
                            <CardDescription>{chapter.description}</CardDescription>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline">{chapter.videos?.length || 0} video-uri</Badge>
                          <Button size="sm" variant="ghost" onClick={() => setEditChapterDialog(chapter)}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" className="text-red-600" onClick={() => deleteChapter(chapter.id)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="p-4">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-16">#</TableHead>
                            <TableHead>Titlu</TableHead>
                            <TableHead>YouTube ID</TableHead>
                            <TableHead>Durată</TableHead>
                            <TableHead className="text-right">Acțiuni</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {chapter.videos?.map((video, idx) => (
                            <TableRow key={video.id}>
                              <TableCell>{idx + 1}</TableCell>
                              <TableCell className="font-medium">{video.title}</TableCell>
                              <TableCell>
                                <code className="text-xs bg-slate-100 px-2 py-1 rounded">{video.youtube_id}</code>
                              </TableCell>
                              <TableCell>{video.duration}</TableCell>
                              <TableCell className="text-right">
                                <Button size="sm" variant="ghost" onClick={() => setEditVideoDialog({ chapter, video })}>
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button size="sm" variant="ghost" className="text-red-600" onClick={() => deleteVideo(chapter.id, video.id)}>
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                          {(!chapter.videos || chapter.videos.length === 0) && (
                            <TableRow>
                              <TableCell colSpan={5} className="text-center text-slate-500 py-8">
                                Niciun video în acest capitol
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                      <Button
                        variant="outline"
                        className="w-full mt-4 border-dashed"
                        onClick={() => setNewVideoDialog(chapter.id)}
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Adaugă Video în "{chapter.title}"
                      </Button>
                    </CardContent>
                  </Card>
                ))}

                {videoChapters.length === 0 && (
                  <Card className="border-0 shadow-sm">
                    <CardContent className="py-16 text-center">
                      <Video className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                      <h3 className="font-semibold text-slate-700">Niciun capitol</h3>
                      <p className="text-slate-500 mb-4">Creează primul capitol pentru a adăuga lecții video</p>
                      <Button onClick={() => setNewChapterDialog(true)}>
                        <Plus className="w-4 h-4 mr-2" />
                        Creează Capitol
                      </Button>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}

            {/* Materials Tab */}
            {activeTab === "materials" && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <div>
                    <h2 className="text-xl font-bold">Materiale Descărcabile</h2>
                    <p className="text-slate-500 text-sm">PDF-uri și documente pentru abonați</p>
                  </div>
                  <Button onClick={() => setNewMaterialDialog(true)} className="bg-blue-600 hover:bg-blue-700">
                    <Plus className="w-4 h-4 mr-2" />
                    Adaugă Material
                  </Button>
                </div>

                {/* Materials Grid */}
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {materials.map((material) => (
                    <Card key={material.id} className="border-0 shadow-sm">
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <FileText className="w-6 h-6 text-red-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-slate-900 truncate">{material.title}</h3>
                            <p className="text-sm text-slate-500 truncate">{material.description}</p>
                            <div className="flex items-center gap-2 mt-2">
                              <Badge variant="outline" className="text-xs">{material.category}</Badge>
                              <span className="text-xs text-slate-400">{material.download_count || 0} descărcări</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-2 mt-4">
                          <Button
                            size="sm"
                            variant="outline"
                            className="flex-1"
                            onClick={() => setEditMaterialDialog(material)}
                          >
                            <Edit className="w-3 h-3 mr-1" />
                            Editează
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600"
                            onClick={async () => {
                              if (window.confirm("Ștergi acest material?")) {
                                await axios.delete(`${API}/admin/materials/${material.id}`);
                                setMaterials(prev => prev.filter(m => m.id !== material.id));
                                toast.success("Material șters");
                              }
                            }}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {materials.length === 0 && (
                  <Card className="border-0 shadow-sm">
                    <CardContent className="py-16 text-center">
                      <FileText className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                      <h3 className="font-semibold text-slate-700">Niciun material</h3>
                      <p className="text-slate-500 text-sm">Adaugă PDF-uri pentru abonații tăi</p>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}

            {/* Chapter Quizzes Tab */}
            {activeTab === "quizzes" && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <div>
                    <h2 className="text-xl font-bold">Chestionar Capitol</h2>
                    <p className="text-slate-500 text-sm">Întrebări de verificare după fiecare capitol</p>
                  </div>
                  <Button onClick={() => setNewQuizDialog(true)} className="bg-blue-600 hover:bg-blue-700">
                    <Plus className="w-4 h-4 mr-2" />
                    Adaugă Quiz
                  </Button>
                </div>

                {/* Quizzes List */}
                <div className="space-y-4">
                  {chapterQuizzes.map((quiz) => {
                    const chapter = videoChapters.find(c => c.id === quiz.chapter_id);
                    return (
                      <Card key={quiz.id} className="border-0 shadow-sm">
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                                <HelpCircle className="w-6 h-6 text-purple-600" />
                              </div>
                              <div>
                                <h3 className="font-semibold text-slate-900">{quiz.title}</h3>
                                <p className="text-sm text-slate-500">
                                  Capitol: {chapter?.title || "Necunoscut"} • {quiz.questions?.length || 0} întrebări • Minim {quiz.passing_score}% pentru promovare
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge className={quiz.is_active ? "bg-green-500" : "bg-slate-400"}>
                                {quiz.is_active ? "Activ" : "Inactiv"}
                              </Badge>
                              <Button size="sm" variant="outline" onClick={() => setEditQuizDialog(quiz)}>
                                <Edit className="w-4 h-4 mr-1" />
                                Editează
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-600"
                                onClick={async () => {
                                  if (window.confirm("Ștergi acest quiz?")) {
                                    await axios.delete(`${API}/admin/chapter-quizzes/${quiz.id}`);
                                    setChapterQuizzes(prev => prev.filter(q => q.id !== quiz.id));
                                    toast.success("Quiz șters");
                                  }
                                }}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>

                {chapterQuizzes.length === 0 && (
                  <Card className="border-0 shadow-sm">
                    <CardContent className="py-16 text-center">
                      <HelpCircle className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                      <h3 className="font-semibold text-slate-700">Niciun quiz</h3>
                      <p className="text-slate-500 text-sm">Adaugă quiz-uri pentru capitole</p>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}

            {/* Gallery Tab */}
            {activeTab === "gallery" && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <div>
                    <h2 className="text-xl font-bold">Galerie Foto</h2>
                    <p className="text-slate-500 text-sm">Adaugă poze pentru pagina principală</p>
                  </div>
                  <Button onClick={() => setNewGalleryDialog(true)} className="bg-blue-600 hover:bg-blue-700">
                    <Plus className="w-4 h-4 mr-2" />
                    Adaugă Imagine
                  </Button>
                </div>

                {galleryImages.length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {galleryImages.map((image) => (
                      <Card key={image.id} className="border-0 shadow-sm overflow-hidden group">
                        <div className="relative aspect-square">
                          <img
                            src={image.image_url}
                            alt={image.title || "Gallery image"}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.src = "https://via.placeholder.com/400x400?text=Imagine+Invalida";
                            }}
                          />
                          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                            <Button size="sm" variant="secondary" onClick={() => setEditGalleryDialog(image)}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="destructive" onClick={() => deleteGalleryImage(image.id)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                          {!image.is_active && (
                            <div className="absolute top-2 right-2">
                              <Badge className="bg-yellow-500">Ascunsă</Badge>
                            </div>
                          )}
                        </div>
                        <CardContent className="p-3">
                          <p className="font-medium text-sm truncate">{image.title || "Fără titlu"}</p>
                          <p className="text-xs text-slate-500 truncate">{image.description || "-"}</p>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <Card className="border-0 shadow-sm">
                    <CardContent className="py-16 text-center">
                      <ImageIcon className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                      <h3 className="font-semibold text-slate-700">Nicio imagine în galerie</h3>
                      <p className="text-slate-500 mb-4">Adaugă prima imagine pentru pagina principală</p>
                      <Button onClick={() => setNewGalleryDialog(true)}>
                        <Plus className="w-4 h-4 mr-2" />
                        Adaugă Imagine
                      </Button>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Import Dialog */}
      <Dialog open={importDialog} onOpenChange={setImportDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Import Întrebări din CSV</DialogTitle>
            <DialogDescription>
              Încarcă un fișier CSV cu întrebări. Descarcă template-ul pentru format.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Button variant="outline" onClick={downloadCSVTemplate} className="w-full">
              <Download className="w-4 h-4 mr-2" />
              Descarcă Template CSV
            </Button>
            <div className="border-2 border-dashed rounded-lg p-8 text-center">
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                className="hidden"
                onChange={(e) => {
                  if (e.target.files?.[0]) {
                    handleImportCSV(e.target.files[0]);
                  }
                }}
              />
              <Upload className="w-10 h-10 mx-auto text-slate-400 mb-2" />
              <p className="text-slate-600 mb-2">Trage fișierul aici sau</p>
              <Button onClick={() => fileInputRef.current?.click()}>
                Selectează Fișier
              </Button>
            </div>
            <div className="text-xs text-slate-500">
              <p className="font-semibold mb-1">Coloane CSV:</p>
              <code className="block bg-slate-100 p-2 rounded text-xs">
                quiz_number, category, text, image_url, option_a, option_b, option_c, option_d, correct_answer, explanation
              </code>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Plan Dialog */}
      <EditPlanDialog plan={editPlanDialog} onClose={() => setEditPlanDialog(null)} onSave={updatePlan} />

      {/* Question Dialogs */}
      <QuestionDialog
        question={editQuestionDialog}
        onClose={() => setEditQuestionDialog(null)}
        onSave={(data) => updateQuestion(editQuestionDialog.id, data)}
      />
      <QuestionDialog
        question={newQuestionDialog ? {} : null}
        isNew
        onClose={() => setNewQuestionDialog(false)}
        onSave={createQuestion}
      />

      {/* View User Dialog */}
      {viewUserDialog && (
        <Dialog open={!!viewUserDialog} onOpenChange={() => setViewUserDialog(null)}>
          <DialogContent>
            <DialogHeader><DialogTitle>Detalii Utilizator</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><Label className="text-slate-500">Nume</Label><p className="font-medium">{viewUserDialog.name}</p></div>
                <div><Label className="text-slate-500">Email</Label><p className="font-medium">{viewUserDialog.email}</p></div>
                <div><Label className="text-slate-500">Telefon</Label><p className="font-medium">{viewUserDialog.phone || "-"}</p></div>
                <div><Label className="text-slate-500">Rol</Label><p className="font-medium capitalize">{viewUserDialog.role}</p></div>
              </div>
              <div className="pt-4 border-t space-y-2">
                <Label>Acțiuni Abonament</Label>
                <div className="flex gap-2 flex-wrap">
                  <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={() => { activateUserPlan(viewUserDialog.id, "standard"); setViewUserDialog(null); }}>
                    Activează Standard
                  </Button>
                  <Button size="sm" className="bg-gradient-to-r from-yellow-500 to-amber-600 hover:from-yellow-600 hover:to-amber-700" onClick={() => { activateUserPlan(viewUserDialog.id, "premium"); setViewUserDialog(null); }}>
                    Activează Premium
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => { deactivateUserPlan(viewUserDialog.id); setViewUserDialog(null); }}>
                    Dezactivează
                  </Button>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Chapter Dialogs */}
      {newChapterDialog && (
        <ChapterDialog
          chapter={{}}
          isNew={true}
          onClose={() => setNewChapterDialog(false)}
          onSave={(_, data) => createChapter(data)}
        />
      )}
      {editChapterDialog && (
        <ChapterDialog
          chapter={editChapterDialog}
          isNew={false}
          onClose={() => setEditChapterDialog(null)}
          onSave={(id, data) => updateChapter(id, data)}
        />
      )}

      {/* Video Dialogs */}
      {newVideoDialog && (
        <VideoDialog
          video={{}}
          chapterId={newVideoDialog}
          isNew={true}
          onClose={() => setNewVideoDialog(null)}
          onSave={(chapterId, _, data) => addVideo(chapterId, data)}
        />
      )}
      {editVideoDialog && (
        <VideoDialog
          video={editVideoDialog.video}
          chapterId={editVideoDialog.chapter.id}
          isNew={false}
          onClose={() => setEditVideoDialog(null)}
          onSave={(chapterId, videoId, data) => updateVideo(chapterId, editVideoDialog.video.id, data)}
        />
      )}

      {/* Gallery Dialogs */}
      {newGalleryDialog && (
        <GalleryDialog
          image={{}}
          isNew={true}
          onClose={() => setNewGalleryDialog(false)}
          onSave={(_, data) => createGalleryImage(data)}
        />
      )}
      {editGalleryDialog && (
        <GalleryDialog
          image={editGalleryDialog}
          isNew={false}
          onClose={() => setEditGalleryDialog(null)}
          onSave={(id, data) => updateGalleryImage(id, data)}
        />
      )}
      {editCategoryDialog && (
        <EditCategoryDialog
          category={editCategoryDialog}
          onClose={() => setEditCategoryDialog(null)}
          onSave={updateCategory}
        />
      )}
      {(editMaterialDialog || newMaterialDialog) && (
        <MaterialDialog
          material={editMaterialDialog}
          isNew={newMaterialDialog}
          onClose={() => { setEditMaterialDialog(null); setNewMaterialDialog(false); }}
          onSave={async (id, data, isNew) => {
            try {
              if (isNew) {
                const res = await axios.post(`${API}/admin/materials`, data);
                setMaterials(prev => [...prev, { id: res.data.id, ...data, download_count: 0 }]);
                toast.success("Material adăugat!");
              } else {
                await axios.patch(`${API}/admin/materials/${id}`, data);
                setMaterials(prev => prev.map(m => m.id === id ? { ...m, ...data } : m));
                toast.success("Material actualizat!");
              }
              setEditMaterialDialog(null);
              setNewMaterialDialog(false);
            } catch (err) {
              toast.error("Eroare la salvare");
            }
          }}
        />
      )}
      {(editQuizDialog || newQuizDialog) && (
        <QuizDialog
          quiz={editQuizDialog}
          isNew={newQuizDialog}
          chapters={videoChapters}
          onClose={() => { setEditQuizDialog(null); setNewQuizDialog(false); }}
          onSave={async (id, data, isNew) => {
            try {
              if (isNew) {
                const res = await axios.post(`${API}/admin/chapter-quizzes`, data);
                setChapterQuizzes(prev => [...prev, { id: res.data.id, ...data }]);
                toast.success("Quiz creat!");
              } else {
                await axios.patch(`${API}/admin/chapter-quizzes/${id}`, data);
                setChapterQuizzes(prev => prev.map(q => q.id === id ? { ...q, ...data } : q));
                toast.success("Quiz actualizat!");
              }
              setEditQuizDialog(null);
              setNewQuizDialog(false);
            } catch (err) {
              toast.error("Eroare la salvare");
            }
          }}
        />
      )}

      {/* New Free Lesson Dialog */}
      <Dialog open={newFreeLessonDialog} onOpenChange={setNewFreeLessonDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Adaugă Lecție Gratuită</DialogTitle>
            <DialogDescription>
              Adaugă un video gratuit pentru vizitatori. Poți folosi link-uri YouTube, Vimeo sau URL direct la video.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={(e) => {
            e.preventDefault();
            const formData = new FormData(e.target);
            createFreeLesson({
              title: formData.get("title"),
              description: formData.get("description"),
              video_url: formData.get("video_url") || null,
              thumbnail_url: formData.get("thumbnail_url") || null,
              duration: formData.get("duration") || null,
              order: parseInt(formData.get("order")) || 0
            });
          }}>
            <div className="space-y-4 py-4">
              <div>
                <Label>Titlu *</Label>
                <Input name="title" required placeholder="Ex: Intersecții cu sens giratoriu" />
              </div>
              <div>
                <Label>Descriere</Label>
                <Textarea name="description" rows={3} placeholder="Scurtă descriere a lecției..." />
              </div>
              <div>
                <Label>URL Video</Label>
                <Input name="video_url" placeholder="https://youtube.com/watch?v=... sau link direct" />
                <p className="text-xs text-slate-500 mt-1">YouTube, Vimeo sau URL direct la fișier video</p>
              </div>
              <div>
                <Label>URL Thumbnail (opțional)</Label>
                <Input name="thumbnail_url" placeholder="https://..." />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Durată</Label>
                  <Input name="duration" placeholder="Ex: 5:30" />
                </div>
                <div>
                  <Label>Ordine</Label>
                  <Input name="order" type="number" defaultValue={freeLessons.length} />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setNewFreeLessonDialog(false)}>Anulează</Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                <Plus className="w-4 h-4 mr-2" />
                Adaugă
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Free Lesson Dialog */}
      <Dialog open={!!editFreeLessonDialog} onOpenChange={() => setEditFreeLessonDialog(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Editează Lecție Gratuită</DialogTitle>
          </DialogHeader>
          {editFreeLessonDialog && (
            <form onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.target);
              updateFreeLesson(editFreeLessonDialog.id, {
                title: formData.get("title"),
                description: formData.get("description"),
                video_url: formData.get("video_url") || null,
                thumbnail_url: formData.get("thumbnail_url") || null,
                duration: formData.get("duration") || null,
                order: parseInt(formData.get("order")) || 0,
                is_active: formData.get("is_active") === "on"
              });
            }}>
              <div className="space-y-4 py-4">
                <div>
                  <Label>Titlu *</Label>
                  <Input name="title" required defaultValue={editFreeLessonDialog.title} />
                </div>
                <div>
                  <Label>Descriere</Label>
                  <Textarea name="description" rows={3} defaultValue={editFreeLessonDialog.description || ""} />
                </div>
                <div>
                  <Label>URL Video</Label>
                  <Input name="video_url" defaultValue={editFreeLessonDialog.video_url || ""} />
                </div>
                <div>
                  <Label>URL Thumbnail</Label>
                  <Input name="thumbnail_url" defaultValue={editFreeLessonDialog.thumbnail_url || ""} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Durată</Label>
                    <Input name="duration" defaultValue={editFreeLessonDialog.duration || ""} />
                  </div>
                  <div>
                    <Label>Ordine</Label>
                    <Input name="order" type="number" defaultValue={editFreeLessonDialog.order || 0} />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <input type="checkbox" name="is_active" id="is_active" defaultChecked={editFreeLessonDialog.is_active} className="rounded" />
                  <Label htmlFor="is_active">Activ (vizibil pe site)</Label>
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setEditFreeLessonDialog(null)}>Anulează</Button>
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                  <Save className="w-4 h-4 mr-2" />
                  Salvează
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ icon: Icon, value, label, color }) {
  const colors = {
    blue: "bg-blue-100 text-blue-600",
    green: "bg-green-100 text-green-600",
    purple: "bg-purple-100 text-purple-600",
    yellow: "bg-yellow-100 text-yellow-600"
  };
  return (
    <Card className="border-0 shadow-sm">
      <CardContent className="p-6">
        <div className="flex items-center gap-4">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${colors[color]}`}>
            <Icon className="w-6 h-6" />
          </div>
          <div>
            <p className="text-2xl font-bold">{value}</p>
            <p className="text-sm text-slate-500">{label}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function EditPlanDialog({ plan, onClose, onSave }) {
  const [price, setPrice] = useState(plan?.price || 0);
  const [testsPerDay, setTestsPerDay] = useState(plan?.tests_per_day || 0);
  const [isActive, setIsActive] = useState(plan?.is_active ?? true);

  useEffect(() => {
    if (plan) {
      setPrice(plan.price);
      setTestsPerDay(plan.tests_per_day);
      setIsActive(plan.is_active);
    }
  }, [plan]);

  if (!plan) return null;

  return (
    <Dialog open={!!plan} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader><DialogTitle>Editează Plan: {plan.name}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><Label>Preț (RON/lună)</Label><Input type="number" value={price} onChange={(e) => setPrice(parseFloat(e.target.value))} /></div>
          <div><Label>Teste pe zi (-1 = nelimitat)</Label><Input type="number" value={testsPerDay} onChange={(e) => setTestsPerDay(parseInt(e.target.value))} /></div>
          <div className="flex items-center justify-between"><Label>Plan Activ</Label><Switch checked={isActive} onCheckedChange={setIsActive} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Anulează</Button>
          <Button onClick={() => onSave(plan.code, { price, tests_per_day: testsPerDay, is_active: isActive })}>
            <Save className="w-4 h-4 mr-2" />Salvează
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const LICENSE_CATS = ["A", "B", "C", "D", "E", "Tr", "R"];
const QUIZZES_PER_LICENSE = { A: 20, B: 47, C: 20, D: 18, E: 7, Tr: 30, R: 23 };

function QuestionDialog({ question, isNew, onClose, onSave }) {
  const [text, setText] = useState("");
  const [licenseCategory, setLicenseCategory] = useState("B");
  const [quizNumber, setQuizNumber] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [explanation, setExplanation] = useState("");
  const [explanationVideoUrl, setExplanationVideoUrl] = useState("");
  const [multipleAnswers, setMultipleAnswers] = useState(false);
  const [options, setOptions] = useState([
    { id: "a", text: "", is_correct: true },
    { id: "b", text: "", is_correct: false },
    { id: "c", text: "", is_correct: false },
  ]);

  useEffect(() => {
    if (question && !isNew) {
      setText(question.text || "");
      setLicenseCategory(question.license_category || "B");
      setQuizNumber(question.quiz_number?.toString() || "");
      setImageUrl(question.image_url || "");
      setExplanation(question.explanation || "");
      setExplanationVideoUrl(question.explanation_video_url || "");
      // Detectăm automat dacă are răspunsuri multiple
      const correctCount = question.options?.filter(o => o.is_correct).length || 0;
      setMultipleAnswers(correctCount > 1 || question.multiple_answers === true);
      setOptions(question.options?.length ? question.options : [
        { id: "a", text: "", is_correct: true },
        { id: "b", text: "", is_correct: false },
        { id: "c", text: "", is_correct: false },
      ]);
    } else if (isNew && question) {
      setText(""); setLicenseCategory("B"); setQuizNumber(""); setImageUrl("");
      setExplanation(""); setExplanationVideoUrl(""); setMultipleAnswers(false);
      setOptions([
        { id: "a", text: "", is_correct: true },
        { id: "b", text: "", is_correct: false },
        { id: "c", text: "", is_correct: false },
      ]);
    }
  }, [question, isNew]);

  const maxQuizzes = QUIZZES_PER_LICENSE[licenseCategory] || 47;

  const handleOptionChange = (index, field, value) => {
    const newOptions = [...options];
    if (field === "is_correct") {
      if (multipleAnswers) {
        // Pentru răspunsuri multiple - toggle checkbox
        newOptions[index].is_correct = !newOptions[index].is_correct;
      } else {
        // Pentru răspuns unic - doar unul poate fi corect
        newOptions.forEach((opt, i) => opt.is_correct = i === index);
      }
    } else {
      newOptions[index][field] = value;
    }
    setOptions(newOptions);
  };

  const addOption = () => {
    if (options.length < 4) {
      const ids = ['a', 'b', 'c', 'd'];
      setOptions([...options, { id: ids[options.length], text: "", is_correct: false }]);
    }
  };

  const handleSave = () => {
    const filledOptions = options.filter(o => o.text);
    const correctOptions = filledOptions.filter(o => o.is_correct);
    
    if (!text || !quizNumber || filledOptions.length < 2) {
      toast.error("Completează întrebarea, numărul chestionarului și minim 2 opțiuni");
      return;
    }
    if (correctOptions.length === 0) {
      toast.error("Selectează cel puțin un răspuns corect");
      return;
    }
    
    onSave({
      text,
      category: "General",
      license_category: licenseCategory,
      quiz_number: quizNumber ? parseInt(quizNumber) : null,
      image_url: imageUrl || null,
      difficulty: "medium",
      explanation,
      explanation_video_url: explanationVideoUrl || null,
      multiple_answers: correctOptions.length > 1,
      options: filledOptions
    });
  };

  if (!question) return null;

  const correctCount = options.filter(o => o.is_correct).length;

  return (
    <Dialog open={!!question} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{isNew ? "Adaugă Întrebare" : "Editează Întrebare"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Categoria Permis *</Label>
              <Select value={licenseCategory} onValueChange={setLicenseCategory}>
                <SelectTrigger><SelectValue placeholder="Selectează" /></SelectTrigger>
                <SelectContent>
                  {LICENSE_CATS.map(cat => <SelectItem key={cat} value={cat}>Cat. {cat}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Chestionar (1-{maxQuizzes}) *</Label>
              <Select value={quizNumber || "none"} onValueChange={(v) => setQuizNumber(v === "none" ? "" : v)}>
                <SelectTrigger><SelectValue placeholder="Selectează" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Selectează...</SelectItem>
                  {Array.from({ length: maxQuizzes }, (_, i) => (
                    <SelectItem key={i} value={String(i + 1)}>{i + 1}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label>URL Imagine (opțional)</Label>
            <Input value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} placeholder="https://..." />
            {imageUrl && <img src={imageUrl} alt="Preview" className="mt-2 max-h-32 rounded" onError={(e) => e.target.style.display = 'none'} />}
          </div>

          <div>
            <Label>Întrebare *</Label>
            <Textarea value={text} onChange={(e) => setText(e.target.value)} placeholder="Textul întrebării..." rows={3} />
          </div>

          <div>
            <div className="flex justify-between items-center mb-2">
              <div className="flex items-center gap-4">
                <Label>Opțiuni *</Label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input 
                    type="checkbox" 
                    checked={multipleAnswers} 
                    onChange={(e) => setMultipleAnswers(e.target.checked)}
                    className="w-4 h-4 rounded"
                  />
                  <span className="text-slate-600">Răspunsuri multiple</span>
                </label>
              </div>
              {options.length < 4 && <Button size="sm" variant="outline" onClick={addOption}><Plus className="w-3 h-3 mr-1" />Opțiune</Button>}
            </div>
            <p className="text-xs text-slate-500 mb-3">
              {multipleAnswers 
                ? `Bifează toate răspunsurile corecte (${correctCount} selectate)` 
                : "Selectează răspunsul corect"}
            </p>
            {options.map((opt, index) => (
              <div key={opt.id} className="flex items-center gap-3 mb-2">
                <input 
                  type={multipleAnswers ? "checkbox" : "radio"} 
                  name="correct" 
                  checked={opt.is_correct} 
                  onChange={() => handleOptionChange(index, "is_correct", true)} 
                  className="w-4 h-4" 
                />
                <span className="font-bold w-6">{opt.id.toUpperCase()}.</span>
                <Input value={opt.text} onChange={(e) => handleOptionChange(index, "text", e.target.value)} placeholder={`Opțiunea ${opt.id.toUpperCase()}`} className="flex-1" />
              </div>
            ))}
          </div>

          <div>
            <Label>Explicație (opțional)</Label>
            <Textarea value={explanation} onChange={(e) => setExplanation(e.target.value)} placeholder="De ce este corect..." rows={2} />
          </div>

          <div>
            <Label>URL Video Explicativ (opțional)</Label>
            <Input value={explanationVideoUrl} onChange={(e) => setExplanationVideoUrl(e.target.value)} placeholder="https://youtube.com/..." />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Anulează</Button>
          <Button onClick={handleSave}><Save className="w-4 h-4 mr-2" />{isNew ? "Adaugă" : "Salvează"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Chapter Dialog Component
function ChapterDialog({ chapter, isNew, onClose, onSave }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [order, setOrder] = useState(1);
  const [requiredPlan, setRequiredPlan] = useState("standard");
  const [theoryContent, setTheoryContent] = useState("");
  const [theoryPreviewLength, setTheoryPreviewLength] = useState(300);
  const [imageUrl, setImageUrl] = useState("");
  const [activeSection, setActiveSection] = useState("basic"); // basic, theory

  useEffect(() => {
    if (chapter && !isNew) {
      setTitle(chapter.title || "");
      setDescription(chapter.description || "");
      setOrder(chapter.order || 1);
      setRequiredPlan(chapter.required_plan || "standard");
      setTheoryContent(chapter.theory_content || "");
      setTheoryPreviewLength(chapter.theory_preview_length || 300);
      setImageUrl(chapter.image_url || "");
    } else {
      setTitle("");
      setDescription("");
      setOrder(1);
      setRequiredPlan("standard");
      setTheoryContent("");
      setTheoryPreviewLength(300);
      setImageUrl("");
    }
  }, [chapter, isNew]);

  const handleSave = () => {
    if (!title.trim()) {
      toast.error("Titlul este obligatoriu");
      return;
    }
    onSave(isNew ? null : chapter.id, {
      title,
      description,
      order,
      required_plan: requiredPlan,
      theory_content: theoryContent,
      theory_preview_length: theoryPreviewLength,
      image_url: imageUrl
    });
  };

  return (
    <Dialog open={!!chapter || isNew} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isNew ? "Capitol Nou" : "Editeaza Capitol"}</DialogTitle>
          <DialogDescription>
            Completeaza informatiile despre capitol si teoria aferenta
          </DialogDescription>
        </DialogHeader>

        {/* Section Tabs */}
        <div className="flex gap-2 border-b pb-2 mb-4">
          <Button
            variant={activeSection === "basic" ? "default" : "ghost"}
            size="sm"
            onClick={() => setActiveSection("basic")}
          >
            Informatii de Baza
          </Button>
          <Button
            variant={activeSection === "theory" ? "default" : "ghost"}
            size="sm"
            onClick={() => setActiveSection("theory")}
          >
            Teorie (HTML)
          </Button>
        </div>

        {activeSection === "basic" && (
          <div className="space-y-4">
            <div>
              <Label>Titlu Capitol</Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Ex: Indicatoare Rutiere" />
            </div>
            <div>
              <Label>Descriere</Label>
              <Textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Scurta descriere a capitolului..." rows={2} />
            </div>
            <div>
              <Label>URL Imagine (optional)</Label>
              <Input value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} placeholder="https://..." />
              {imageUrl && (
                <img src={imageUrl} alt="Preview" className="mt-2 w-32 h-20 object-cover rounded border" />
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Ordine (pentru sortare)</Label>
                <Input type="number" value={order} onChange={(e) => setOrder(parseInt(e.target.value) || 1)} min={0} />
              </div>
              <div>
                <Label>Lungime Preview (caractere)</Label>
                <Input type="number" value={theoryPreviewLength} onChange={(e) => setTheoryPreviewLength(parseInt(e.target.value) || 300)} min={100} max={1000} />
                <p className="text-xs text-slate-500 mt-1">Cat vad utilizatorii fara abonament</p>
              </div>
            </div>
            <div>
              <Label>Pachet necesar pentru acces complet</Label>
              <select
                value={requiredPlan}
                onChange={(e) => setRequiredPlan(e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                <option value="start">Start (Gratuit)</option>
                <option value="standard">Standard - Teorie + Quiz</option>
                <option value="premium">Premium - Teorie + Video + Quiz</option>
              </select>
            </div>
          </div>
        )}

        {activeSection === "theory" && (
          <div className="space-y-4">
            <div>
              <Label>Continut Teorie (HTML)</Label>
              <p className="text-xs text-slate-500 mb-2">
                Foloseste tag-uri HTML: &lt;h2&gt;, &lt;h3&gt;, &lt;p&gt;, &lt;ul&gt;, &lt;li&gt;, &lt;strong&gt;, &lt;em&gt;
              </p>
              <Textarea
                value={theoryContent}
                onChange={(e) => setTheoryContent(e.target.value)}
                placeholder="<h2>Titlu Sectiune</h2>
<p>Text explicativ...</p>
<h3>Subtitlu</h3>
<ul>
<li>Element lista</li>
</ul>"
                rows={15}
                className="font-mono text-sm"
              />
            </div>
            {theoryContent && (
              <div>
                <Label>Preview Teorie</Label>
                <div className="mt-2 p-4 bg-slate-50 rounded-lg border max-h-64 overflow-y-auto prose prose-sm">
                  <div dangerouslySetInnerHTML={{ __html: theoryContent }} />
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Anuleaza</Button>
          <Button onClick={handleSave}>
            <Save className="w-4 h-4 mr-2" />{isNew ? "Creeaza" : "Salveaza"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Video Dialog Component
function VideoDialog({ video, chapterId, isNew, onClose, onSave }) {
  const [title, setTitle] = useState("");
  const [youtubeId, setYoutubeId] = useState("");
  const [duration, setDuration] = useState("");

  useEffect(() => {
    if (video && !isNew) {
      setTitle(video.title || "");
      setYoutubeId(video.youtube_id || "");
      setDuration(video.duration || "");
    } else {
      setTitle("");
      setYoutubeId("");
      setDuration("");
    }
  }, [video, isNew]);

  const extractYoutubeId = (input) => {
    // Handle full URLs or just IDs
    const match = input.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\s]+)/);
    return match ? match[1] : input;
  };

  const handleSave = () => {
    if (!title.trim() || !youtubeId.trim()) {
      toast.error("Titlul și YouTube ID sunt obligatorii");
      return;
    }
    const extractedId = extractYoutubeId(youtubeId);
    onSave(chapterId, isNew ? null : video.id, { title, youtube_id: extractedId, duration: duration || "0:00" });
  };

  return (
    <Dialog open={!!video || isNew} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isNew ? "Adaugă Video" : "Editează Video"}</DialogTitle>
          <DialogDescription>
            Introdu ID-ul YouTube sau URL-ul complet al video-ului
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Titlu Video</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Ex: Indicatoare de avertizare" />
          </div>
          <div>
            <Label>YouTube ID sau URL</Label>
            <Input value={youtubeId} onChange={(e) => setYoutubeId(e.target.value)} placeholder="dQw4w9WgXcQ sau https://youtube.com/watch?v=..." />
            <p className="text-xs text-slate-500 mt-1">Poți copia întregul link de pe YouTube</p>
          </div>
          <div>
            <Label>Durată (ex: 12:30)</Label>
            <Input value={duration} onChange={(e) => setDuration(e.target.value)} placeholder="10:30" />
          </div>
          {youtubeId && (
            <div className="border rounded-lg p-2 bg-slate-50">
              <p className="text-xs text-slate-500 mb-2">Preview:</p>
              <div className="aspect-video bg-black rounded overflow-hidden">
                <iframe
                  width="100%"
                  height="100%"
                  src={`https://www.youtube.com/embed/${extractYoutubeId(youtubeId)}`}
                  frameBorder="0"
                  allowFullScreen
                />
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Anulează</Button>
          <Button onClick={handleSave}>
            <Save className="w-4 h-4 mr-2" />{isNew ? "Adaugă" : "Salvează"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Gallery Dialog Component
function GalleryDialog({ image, isNew, onClose, onSave }) {
  const [imageUrl, setImageUrl] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [order, setOrder] = useState(1);
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    if (image && !isNew) {
      setImageUrl(image.image_url || "");
      setTitle(image.title || "");
      setDescription(image.description || "");
      setOrder(image.order || 1);
      setIsActive(image.is_active !== false);
    } else {
      setImageUrl("");
      setTitle("");
      setDescription("");
      setOrder(1);
      setIsActive(true);
    }
  }, [image, isNew]);

  const handleSave = () => {
    if (!imageUrl.trim()) {
      toast.error("URL-ul imaginii este obligatoriu");
      return;
    }
    onSave(isNew ? null : image.id, {
      image_url: imageUrl,
      title,
      description,
      order,
      is_active: isActive
    });
  };

  return (
    <Dialog open={!!image || isNew} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{isNew ? "Adaugă Imagine" : "Editează Imagine"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>URL Imagine *</Label>
            <Input
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
              placeholder="https://example.com/image.jpg"
            />
            <p className="text-xs text-slate-500 mt-1">
              Introdu URL-ul unei imagini (de pe Google Drive, Imgur, etc.)
            </p>
          </div>

          {imageUrl && (
            <div className="border rounded-lg p-2 bg-slate-50">
              <p className="text-xs text-slate-500 mb-2">Preview:</p>
              <img
                src={imageUrl}
                alt="Preview"
                className="w-full max-h-48 object-contain rounded"
                onError={(e) => {
                  e.target.src = "https://via.placeholder.com/400x300?text=URL+Invalid";
                }}
              />
            </div>
          )}

          <div>
            <Label>Titlu (opțional)</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Ex: Lecție practică de conducere"
            />
          </div>

          <div>
            <Label>Descriere (opțional)</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Scurtă descriere a imaginii..."
              rows={2}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Ordine</Label>
              <Input
                type="number"
                value={order}
                onChange={(e) => setOrder(parseInt(e.target.value) || 1)}
                min={1}
              />
            </div>
            <div className="flex items-center justify-between pt-6">
              <Label>Vizibilă pe site</Label>
              <Switch checked={isActive} onCheckedChange={setIsActive} />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Anulează</Button>
          <Button onClick={handleSave}>
            <Save className="w-4 h-4 mr-2" />{isNew ? "Adaugă" : "Salvează"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// Edit Category Dialog Component
function EditCategoryDialog({ category, onClose, onSave }) {
  const [name, setName] = useState(category?.name || "");
  const [description, setDescription] = useState(category?.description || "");
  const [duration, setDuration] = useState(category?.duration || "");
  const [priceMin, setPriceMin] = useState(category?.price_min || 0);
  const [priceMax, setPriceMax] = useState(category?.price_max || 0);
  const [promoPriceMin, setPromoPriceMin] = useState(category?.promo_price_min || "");
  const [promoPriceMax, setPromoPriceMax] = useState(category?.promo_price_max || "");
  const [promoText, setPromoText] = useState(category?.promo_text || "");
  const [requirements, setRequirements] = useState(category?.requirements?.join("\n") || "");

  useEffect(() => {
    if (category) {
      setName(category.name || "");
      setDescription(category.description || "");
      setDuration(category.duration || "");
      setPriceMin(category.price_min || 0);
      setPriceMax(category.price_max || 0);
      setPromoPriceMin(category.promo_price_min || "");
      setPromoPriceMax(category.promo_price_max || "");
      setPromoText(category.promo_text || "");
      setRequirements(category.requirements?.join("\n") || "");
    }
  }, [category]);

  const handleSave = () => {
    const data = {
      name,
      description,
      duration,
      price_min: parseInt(priceMin) || 0,
      price_max: parseInt(priceMax) || 0,
      promo_price_min: promoPriceMin ? parseInt(promoPriceMin) : null,
      promo_price_max: promoPriceMax ? parseInt(promoPriceMax) : null,
      promo_text: promoText,
      requirements: requirements.split("\n").filter(r => r.trim())
    };
    onSave(category.id, data);
  };

  if (!category) return null;

  return (
    <Dialog open={!!category} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Edit className="w-5 h-5" />
            Editează {category.name}
          </DialogTitle>
          <DialogDescription>
            Modifică prețurile, descrierea și setează promoții
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
          <div>
            <Label>Nume categorie</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </div>

          <div>
            <Label>Descriere</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
            />
          </div>

          <div>
            <Label>Durată curs</Label>
            <Input
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              placeholder="ex: 2-3 luni"
            />
          </div>

          <div className="p-4 bg-slate-50 rounded-lg space-y-4">
            <h4 className="font-semibold text-sm flex items-center gap-2">
              <DollarSign className="w-4 h-4" />
              Prețuri Standard
            </h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Preț minim (RON)</Label>
                <Input
                  type="number"
                  value={priceMin}
                  onChange={(e) => setPriceMin(e.target.value)}
                />
              </div>
              <div>
                <Label>Preț maxim (RON)</Label>
                <Input
                  type="number"
                  value={priceMax}
                  onChange={(e) => setPriceMax(e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="p-4 bg-green-50 rounded-lg space-y-4">
            <h4 className="font-semibold text-sm flex items-center gap-2 text-green-700">
              <Percent className="w-4 h-4" />
              Prețuri Promoționale
            </h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Preț promo minim (RON)</Label>
                <Input
                  type="number"
                  value={promoPriceMin}
                  onChange={(e) => setPromoPriceMin(e.target.value)}
                  placeholder="Lasă gol pentru fără promoție"
                />
              </div>
              <div>
                <Label>Preț promo maxim (RON)</Label>
                <Input
                  type="number"
                  value={promoPriceMax}
                  onChange={(e) => setPromoPriceMax(e.target.value)}
                  placeholder="Lasă gol pentru fără promoție"
                />
              </div>
            </div>
            <div>
              <Label>Text promoție (badge)</Label>
              <Input
                value={promoText}
                onChange={(e) => setPromoText(e.target.value)}
                placeholder="ex: -20% Ofertă de Iarnă!"
              />
            </div>
          </div>

          <div>
            <Label>Cerințe (una pe linie)</Label>
            <Textarea
              value={requirements}
              onChange={(e) => setRequirements(e.target.value)}
              rows={4}
              placeholder="Vârsta minimă 18 ani&#10;Fișă medicală&#10;Aviz psihologic"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Anulează</Button>
          <Button onClick={handleSave} className="bg-blue-600 hover:bg-blue-700">
            <Save className="w-4 h-4 mr-2" />
            Salvează Modificările
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// Material Dialog Component
function MaterialDialog({ material, isNew, onClose, onSave }) {
  const [title, setTitle] = useState(material?.title || "");
  const [description, setDescription] = useState(material?.description || "");
  const [fileUrl, setFileUrl] = useState(material?.file_url || "");
  const [category, setCategory] = useState(material?.category || "general");
  const [order, setOrder] = useState(material?.order || 0);

  useEffect(() => {
    if (material) {
      setTitle(material.title || "");
      setDescription(material.description || "");
      setFileUrl(material.file_url || "");
      setCategory(material.category || "general");
      setOrder(material.order || 0);
    }
  }, [material]);

  const handleSave = () => {
    if (!title || !fileUrl) {
      return;
    }
    const data = {
      title,
      description,
      file_url: fileUrl,
      category,
      order: parseInt(order) || 0
    };
    onSave(isNew ? null : material.id, data, isNew);
  };

  return (
    <Dialog open={!!material || isNew} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            {isNew ? "Adaugă Material Nou" : "Editează Material"}
          </DialogTitle>
          <DialogDescription>
            PDF-uri și documente descărcabile pentru abonați
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div>
            <Label>Titlu *</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="ex: Legislație Rutieră 2024"
            />
          </div>
          <div>
            <Label>Descriere</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Scurtă descriere a materialului"
              rows={2}
            />
          </div>
          <div>
            <Label>URL Fișier (Google Drive, Dropbox, etc.) *</Label>
            <Input
              value={fileUrl}
              onChange={(e) => setFileUrl(e.target.value)}
              placeholder="https://drive.google.com/..."
            />
            <p className="text-xs text-slate-500 mt-1">
              Încarcă fișierul pe Google Drive și pune link-ul direct de descărcare
            </p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Categorie</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">General</SelectItem>
                  <SelectItem value="legislatie">Legislație</SelectItem>
                  <SelectItem value="semne">Semne Rutiere</SelectItem>
                  <SelectItem value="reguli">Reguli de Circulație</SelectItem>
                  <SelectItem value="mecanica">Mecanică Auto</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Ordine</Label>
              <Input
                type="number"
                value={order}
                onChange={(e) => setOrder(e.target.value)}
              />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Anulează</Button>
          <Button onClick={handleSave} className="bg-blue-600 hover:bg-blue-700" disabled={!title || !fileUrl}>
            <Save className="w-4 h-4 mr-2" />
            {isNew ? "Adaugă" : "Salvează"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Quiz Dialog Component
function QuizDialog({ quiz, isNew, onClose, onSave, chapters }) {
  const [title, setTitle] = useState(quiz?.title || "Quiz Capitol");
  const [chapterId, setChapterId] = useState(quiz?.chapter_id || "");
  const [passingScore, setPassingScore] = useState(quiz?.passing_score || 80);
  const [questions, setQuestions] = useState(quiz?.questions || []);
  const [isActive, setIsActive] = useState(quiz?.is_active !== false);

  useEffect(() => {
    if (quiz) {
      setTitle(quiz.title || "Quiz Capitol");
      setChapterId(quiz.chapter_id || "");
      setPassingScore(quiz.passing_score || 80);
      setQuestions(quiz.questions || []);
      setIsActive(quiz.is_active !== false);
    }
  }, [quiz]);

  const addQuestion = () => {
    setQuestions([...questions, {
      question: "",
      image_url: "",
      options: ["", "", "", ""],
      correct: ["a"],
      explanation_video_url: ""
    }]);
  };

  const updateQuestion = (idx, field, value) => {
    const updated = [...questions];
    if (field === "option") {
      updated[idx].options[value.index] = value.text;
    } else {
      updated[idx][field] = value;
    }
    setQuestions(updated);
  };

  const removeQuestion = (idx) => {
    setQuestions(questions.filter((_, i) => i !== idx));
  };

  const handleSave = () => {
    if (!chapterId || questions.length === 0) {
      return;
    }
    const data = {
      title,
      chapter_id: chapterId,
      passing_score: parseInt(passingScore) || 80,
      questions,
      is_active: isActive
    };
    onSave(isNew ? null : quiz.id, data, isNew);
  };

  return (
    <Dialog open={!!quiz || isNew} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <HelpCircle className="w-5 h-5" />
            {isNew ? "Adaugă Quiz Nou" : "Editează Quiz"}
          </DialogTitle>
          <DialogDescription>
            Quiz de verificare la finalul capitolului
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Titlu Quiz</Label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>
            <div>
              <Label>Capitol *</Label>
              <Select value={chapterId} onValueChange={setChapterId}>
                <SelectTrigger><SelectValue placeholder="Selectează capitolul" /></SelectTrigger>
                <SelectContent>
                  {chapters.map(ch => (
                    <SelectItem key={ch.id} value={ch.id}>{ch.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Scor minim promovare (%)</Label>
              <Input
                type="number"
                min="0"
                max="100"
                value={passingScore}
                onChange={(e) => setPassingScore(e.target.value)}
              />
            </div>
            <div className="flex items-center gap-2 pt-6">
              <Switch checked={isActive} onCheckedChange={setIsActive} />
              <Label>Quiz activ</Label>
            </div>
          </div>

          <div className="border-t pt-4">
            <div className="flex justify-between items-center mb-4">
              <Label className="text-base font-semibold">Întrebări ({questions.length})</Label>
              <Button type="button" size="sm" onClick={addQuestion}>
                <Plus className="w-4 h-4 mr-1" />
                Adaugă Întrebare
              </Button>
            </div>

            <div className="space-y-4">
              {questions.map((q, idx) => (
                <Card key={idx} className="border">
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start mb-3">
                      <Label className="font-semibold">Întrebarea {idx + 1}</Label>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="text-red-600"
                        onClick={() => removeQuestion(idx)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                    <Input
                      value={q.question}
                      onChange={(e) => updateQuestion(idx, "question", e.target.value)}
                      placeholder="Scrie întrebarea..."
                      className="mb-3"
                    />
                    <Input
                      value={q.image_url || ""}
                      onChange={(e) => updateQuestion(idx, "image_url", e.target.value)}
                      placeholder="Link imagine (optional)"
                      className="mb-3"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Link direct către imagine (.jpg, .png). Exemplu: i.imgur.com/xxxx.jpg
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                      {["a", "b", "c", "d"].map((letter, optIdx) => (
                        <div key={letter} className="flex items-center gap-2">
                          <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${q.correct === letter ? "bg-green-500 text-white" : "bg-slate-200"
                            }`}>{letter.toUpperCase()}</span>
                          <Input
                            value={q.options[optIdx] || ""}
                            onChange={(e) => updateQuestion(idx, "option", { index: optIdx, text: e.target.value })}
                            placeholder={`Opțiunea ${letter.toUpperCase()}`}
                            className="flex-1"
                          />
                        </div>
                      ))}
                    </div>
                    <div className="mt-3">
                      <Label className="text-sm">Răspuns corect:</Label>
                      <div className="flex gap-2 mt-1">
                        {["a", "b", "c", "d"].map(letter => (
                          <Button
                            key={letter}
                            type="button"
                            size="sm"
                            variant={
                              Array.isArray(q.correct)
                                ? q.correct.includes(letter)
                                  ? "default"
                                  : "outline"
                                : q.correct === letter
                                  ? "default"
                                  : "outline"
                            }

                            className={
                              Array.isArray(q.correct)
                                ? q.correct.includes(letter)
                                  ? "bg-green-600"
                                  : ""
                                : q.correct === letter
                                  ? "bg-green-600"
                                  : ""
                            }
                            onClick={() => {
                              const current = q.correct;

                              if (Array.isArray(current)) {
                                const exists = current.includes(letter);

                                const updated = exists
                                  ? current.filter(l => l !== letter)
                                  : [...current, letter];

                                updateQuestion(idx, "correct", updated);
                              } else {
                                if (current === letter) {
                                  updateQuestion(idx, "correct", []);
                                } else {
                                  updateQuestion(idx, "correct", [letter]);
                                }
                              }
                            }}
                          >
                            {letter.toUpperCase()}
                          </Button>
                        ))}
                      </div>
                    </div>
                    <div className="mt-3 pt-3 border-t">
                      <Label className="text-sm flex items-center gap-2">
                        <Video className="w-4 h-4 text-blue-600" />
                        Video Explicativ (opțional)
                      </Label>
                      <Input
                        value={q.explanation_video_url || ""}
                        onChange={(e) => updateQuestion(idx, "explanation_video_url", e.target.value)}
                        placeholder="https://youtube.com/watch?v=... sau link direct"
                        className="mt-1"
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        Link YouTube pentru explicație video la această întrebare
                      </p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Anulează</Button>
          <Button
            onClick={handleSave}
            className="bg-blue-600 hover:bg-blue-700"
            disabled={!chapterId || questions.length === 0}
          >
            <Save className="w-4 h-4 mr-2" />
            {isNew ? "Creează Quiz" : "Salvează"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}